import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Package, 
  Settings,
  Settings2,
  XCircle,
  Loader2,
  Tag,
  Briefcase,
  AlertCircle,
  Check,
  RotateCcw,
  Upload,
  Calendar,
  Trash2,
  Trash,
  CheckCircle2,
  X,
  Store,
  Edit2,
  FolderTree,
  Hash,
  FileText
} from 'lucide-react';
import { notify } from '../services/notify';
import ItemImport from './ItemImport';
import ItemProfileView from './ItemProfileView';
import DocumentChatterPanel from './DocumentChatterPanel';
import { 
  db, 
  auth, 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  getDocFromServer,
  getDoc,
  limit,
  storage,
  ref,
  uploadBytes,
  getDownloadURL
} from '../firebase';
import { useExclusiveDropdown } from '../lib/dropdowns';
import { getDefaultCurrency } from '../lib/currency';
import {
  type UOM,
  compatibleUoms,
  getProductUomId,
  resolveUom,
  withProductUom,
} from '../lib/uom';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

const getLocalAuthToken = () =>
  typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;

const getLocalApiHeaders = () => {
  const token = getLocalAuthToken();
  return token ? {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  } : {};
};

const uploadLocalItemImage = async (file: File) => {
  const token = getLocalAuthToken();
  if (!token) throw new Error('Local session token is unavailable');

  const formData = new FormData();
  formData.append('file', file);
  formData.append('targetPath', `items/${Date.now()}_${file.name}`);

  const response = await fetch('/api/files/upload', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || 'Failed to upload image');

  const uploadedFile = result.file || result;
  const imageUrl = uploadedFile.url || uploadedFile.downloadUrl || uploadedFile.downloadURL;
  if (!imageUrl) throw new Error('Upload did not return an image URL');

  return imageUrl as string;
};

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  notify.error(`Error: ${error instanceof Error ? error.message : 'An unexpected error occurred'}`);
}
import { clsx, type ClassValue } from 'clsx';
import { SearchableSelect } from './ui/SearchableSelect';
import { twMerge } from 'tailwind-merge';
import HistoryNotesView from './HistoryNotesView';
import { logHistory } from '../services/historyService';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

import { 
  Item, 
  RecipeItem, 
  Recipe, 
  VariantOption, 
  Variant, 
  StoreLocation,
  StoreAvailability,
  Lot,
  ProductCategory
} from '../types';

interface ActionLogEntry {
  action: string;
  details?: string;
  by: string;
  byEmail: string;
  at: any;
}

export default function ItemsView({ orgId, initialItemId, onClearInitial, onNavigate, language = 'en', initialFilter, isInventoryMode, isReportsMode, initialSubTab } : { language?: string, orgId: string, initialItemId?: string | null, onClearInitial?: () => void, onNavigate?: (tab: string, id: string) => void, initialFilter?: 'all' | 'product' | 'service' | 'inventory' | 'archived' | 'reporting' | 'movement' | 'movement-by-delivery' | 'direct-delivery', isInventoryMode?: boolean, isReportsMode?: boolean, initialSubTab?: 'all' | 'scrap' | 'warehouses' | 'lots' | 'categories' | 'reporting' | 'movement' | 'movement-by-delivery' | 'direct-delivery' }) {
  const [items, setItems] = useState<Item[]>([]);
  const [uoms, setUoms] = useState<UOM[]>([]);
  const [movementItemIds, setMovementItemIds] = useState<Set<string>>(new Set());
  const [stores, setStores] = useState<StoreLocation[]>([]);
  const [lotNumbers, setLotNumbers] = useState<any[]>([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  
  const [inventorySubTab, setInventorySubTab] = useState<'all' | 'warehouses' | 'lots' | 'categories' | 'scrap' | 'reporting' | 'movement' | 'movement-by-delivery' | 'direct-delivery'>(initialSubTab || (isReportsMode ? 'movement' : isInventoryMode ? 'all' : 'categories'));
  
  useEffect(() => {
    if (initialSubTab) {
      setInventorySubTab(initialSubTab);
    }
  }, [initialSubTab]);

  const [scrapData, setScrapData] = useState({
    productId: '',
    warehouseId: '',
    quantity: 1,
    action: 'write-off' as 'write-off' | 'salvage',
    targetProductId: '',
    reason: '',
    lotNumber: ''
  });
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', description: '', status: 'active' as 'active' | 'archived' });

  useEffect(() => {
    if (!orgId) return;
    if (getLocalAuthToken()) {
      let cancelled = false;
      fetch(`/api/organizations/${orgId}/product-categories`, { headers: getLocalApiHeaders() })
        .then(r => r.json())
        .then(data => { if (!cancelled) setCategories(Array.isArray(data) ? data : []); })
        .catch(err => { if (!cancelled) console.error('Product categories load failed:', err); });
      return () => { cancelled = true; };
    }
    const q = query(collection(db, 'product_categories'), where('orgId', '==', orgId));
    return onSnapshot(q, (snapshot) => {
      setCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ProductCategory)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'product_categories');
    });
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    if (getLocalAuthToken()) {
      let cancelled = false;
      fetch(`/api/organizations/${orgId}/stores`, { headers: getLocalApiHeaders() })
        .then(r => r.json())
        .then(data => {
          if (cancelled) return;
          // In local mode, the backend stores warehouses and stores in a single
          // `stores` table with no type discriminator, so we surface every record
          // as both a store and a warehouse. This makes them visible in the
          // Warehouses tab; the Firestore path keeps warehouse/store separate.
          const combined = Array.isArray(data) ? data.map((s: any) => ({ ...s, isStore: true, isWarehouse: true })) : [];
          setStores([...combined].sort((a: any, b: any) => a.name.localeCompare(b.name)));
        })
        .catch(err => { if (!cancelled) console.error('Stores/warehouses load failed:', err); });
      return () => { cancelled = true; };
    }

    const warehousesQuery = query(collection(db, 'warehouses'), where('orgId', '==', orgId));
    const storesQuery = query(collection(db, 'stores'), where('orgId', '==', orgId));

    let localWarehouses: any[] = [];
    let localStores: any[] = [];

    const updateCombined = () => {
      const combined = [...localWarehouses.map(w => ({...w, isWarehouse: true})), ...localStores.map(s => ({...s, isStore: true}))];
      setStores([...combined].sort((a, b) => a.name.localeCompare(b.name)));
    };

    const unsubW = onSnapshot(warehousesQuery, (snapshot) => {
      localWarehouses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StoreLocation));
      updateCombined();
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'warehouses');
    });

    const unsubS = onSnapshot(storesQuery, (snapshot) => {
      localStores = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StoreLocation));
      updateCombined();
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'stores');
    });

    return () => { unsubW(); unsubS(); };
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;

    if (getLocalAuthToken()) {
      let cancelled = false;

      fetch(`/api/organizations/${orgId}/lots`, {
        headers: getLocalApiHeaders()
      })
        .then(async (response) => {
          if (!response.ok) throw new Error('Unable to load lot numbers');
          return response.json();
        })
        .then((data) => {
          if (!cancelled) setLotNumbers(data);
        })
        .catch((error) => {
          if (!cancelled) {
            console.error('Local API error:', error);
            notify.error(error instanceof Error ? error.message : 'Unable to load lot numbers');
          }
        });

      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'lot_numbers'),
      where('orgId', '==', orgId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setLotNumbers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'lot_numbers');
    });

    return () => unsubscribe();
  }, [orgId]);
  const [loading, setLoading] = useState(true);
  const [isLotModalOpen, setIsLotModalOpen] = useState(false);
  const [editingLot, setEditingLot] = useState<any>(null);
  
  const [lotFormData, setLotFormData] = useState({ lotNumber: '', expiryDate: '', notes: '', productId: '' });
  const [warehouseFormData, setWarehouseFormData] = useState({ name: '', address: '', status: 'active' as 'active' | 'inactive' | 'archived' });
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<any>(null);

  const handleWarehouseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (getLocalAuthToken()) {
        // In local mode, warehouses are stored as stores
        const method = editingWarehouse ? 'PATCH' : 'POST';
        const url = editingWarehouse
          ? `/api/organizations/${orgId}/stores/${editingWarehouse.id}`
          : `/api/organizations/${orgId}/stores`;
        const r = await fetch(url, { method, headers: getLocalApiHeaders(), body: JSON.stringify({ ...warehouseFormData, orgId }) });
        if (!r.ok) { const e = await r.json().catch(() => null); throw new Error(e?.error || 'Could not save warehouse'); }
        const saved = await r.json();
        setStores(prev => {
          const updated = editingWarehouse
            ? prev.map(s => s.id === saved.id ? { ...saved, isStore: true, isWarehouse: true } : s)
            : [...prev, { ...saved, isStore: true, isWarehouse: true }].sort((a, b) => a.name.localeCompare(b.name));
          return updated;
        });
        notify.confirm(editingWarehouse ? 'Warehouse updated successfully' : 'Warehouse created successfully');
        setIsWarehouseModalOpen(false);
        setEditingWarehouse(null);
        setWarehouseFormData({ name: '', address: '', status: 'active' });
        return;
      }
      if (editingWarehouse) {
        await updateDoc(doc(db, 'warehouses', editingWarehouse.id), {
          ...warehouseFormData,
          updatedAt: serverTimestamp()
        });
        notify.confirm("Warehouse updated successfully");
      } else {
        await addDoc(collection(db, 'warehouses'), {
          ...warehouseFormData,
          orgId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        notify.confirm("Warehouse created successfully");
      }
      setIsWarehouseModalOpen(false);
      setEditingWarehouse(null);
      setWarehouseFormData({ name: '', address: '', status: 'active' });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'warehouses');
    }
  };

  const handleLotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (getLocalAuthToken()) {
        const response = await fetch(
          editingLot
            ? `/api/organizations/${orgId}/lots/${editingLot.id}`
            : `/api/organizations/${orgId}/lots`,
          {
            method: editingLot ? 'PATCH' : 'POST',
            headers: getLocalApiHeaders(),
            body: JSON.stringify(lotFormData)
          }
        );
        if (!response.ok) throw new Error(editingLot ? 'Unable to update lot number' : 'Unable to create lot number');

        const lot = await response.json();
        setLotNumbers(currentLots => editingLot
          ? currentLots.map(currentLot => currentLot.id === lot.id ? lot : currentLot)
          : [...currentLots, lot]
        );
        notify.confirm(editingLot ? 'Lot number updated' : 'Lot number created');
      } else if (editingLot) {
        await updateDoc(doc(db, 'lot_numbers', editingLot.id), {
          ...lotFormData,
          updatedAt: serverTimestamp()
        });
        notify.confirm('Lot number updated');
      } else {
        await addDoc(collection(db, 'lot_numbers'), {
          ...lotFormData,
          orgId,
          createdAt: serverTimestamp()
        });
        notify.confirm('Lot number created');
      }
      setIsLotModalOpen(false);
      setEditingLot(null);
      setLotFormData({ lotNumber: '', expiryDate: '', notes: '', productId: '' });
    } catch (error) {
      if (getLocalAuthToken()) {
        console.error('Local API error:', error);
        notify.error(error instanceof Error ? error.message : 'Unable to save lot number');
      } else {
        handleFirestoreError(error, OperationType.WRITE, 'lot_numbers');
      }
    }
  };

  const handleDeleteLot = async (id: string) => {
    if (!window.confirm('Delete this lot number?')) return;
    try {
      if (getLocalAuthToken()) {
        const response = await fetch(`/api/organizations/${orgId}/lots/${id}`, {
          method: 'DELETE',
          headers: getLocalApiHeaders()
        });
        if (!response.ok) throw new Error('Unable to delete lot number');

        setLotNumbers(currentLots => currentLots.filter(lot => lot.id !== id));
        notify.confirm('Lot number deleted');
        return;
      }

      await deleteDoc(doc(db, 'lot_numbers', id));
      notify.confirm('Lot number deleted');
    } catch (err) {
      if (getLocalAuthToken()) console.error('Local API error:', err);
      else console.error(err);
      notify.error('Error deleting lot number');
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (getLocalAuthToken()) {
        const method = editingCategory ? 'PATCH' : 'POST';
        const url = editingCategory
          ? `/api/organizations/${orgId}/product-categories/${editingCategory.id}`
          : `/api/organizations/${orgId}/product-categories`;
        const r = await fetch(url, { method, headers: getLocalApiHeaders(), body: JSON.stringify(categoryFormData) });
        if (!r.ok) { const e = await r.json().catch(() => null); throw new Error(e?.error || 'Could not save category'); }
        const saved = await r.json();
        setCategories(prev => editingCategory ? prev.map(c => c.id === saved.id ? saved : c) : [...prev, saved].sort((a, b) => a.name.localeCompare(b.name)));
        notify.confirm(editingCategory ? 'Category updated' : 'Category created');
        setIsCategoryModalOpen(false);
        setEditingCategory(null);
        setCategoryFormData({ name: '', description: '', status: 'active' });
        return;
      }
      if (editingCategory) {
        await updateDoc(doc(db, 'product_categories', editingCategory.id), {
          ...categoryFormData,
          updatedAt: serverTimestamp()
        });
        notify.confirm("Category updated");
      } else {
        await addDoc(collection(db, 'product_categories'), {
          ...categoryFormData,
          orgId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        notify.confirm("Category created");
      }
      setIsCategoryModalOpen(false);
      setEditingCategory(null);
      setCategoryFormData({ name: '', description: '', status: 'active' });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'product_categories');
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm('Delete this category? Products in this category will become uncategorized.')) return;
    try {
      if (getLocalAuthToken()) {
        const r = await fetch(`/api/organizations/${orgId}/product-categories/${id}`, { method: 'DELETE', headers: getLocalApiHeaders() });
        if (!r.ok) throw new Error('Could not delete category');
        setCategories(prev => prev.filter(c => c.id !== id));
        notify.confirm('Category deleted');
        return;
      }
      await deleteDoc(doc(db, 'product_categories', id));
      notify.confirm('Category deleted');
    } catch (err) {
      notify.error('Error deleting category');
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [viewingItem, setViewingItem] = useState<Item | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [itemTypeFilter, setItemTypeFilter] = useState('all');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minStock, setMinStock] = useState('');
  const [maxStock, setMaxStock] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filter, setFilter] = useState<'all' | 'product' | 'service' | 'inventory' | 'archived' | 'reporting' | 'movement' | 'movement-by-delivery' | 'direct-delivery'>(initialFilter || (isInventoryMode ? 'inventory' : 'product'));
  const [reportFilters, setReportFilters] = useState({
    productId: '',
    warehouseId: '',
    lotNumber: '',
    expiryBefore: '',
    expiryAfter: '',
    movementType: '',
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [movements, setMovements] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedDelivery, setExpandedDelivery] = useState<string | null>(null);
  const [directMovement, setDirectMovement] = useState({
    type: 'sale' as 'sale' | 'purchase' | 'transfer' | 'return',
    fromWarehouseId: '',
    toWarehouseId: '',
    reference: '',
    notes: '',
    items: [{ productId: '', quantity: 1, lotNumber: '', expiryDate: '' }]
  });

  useEffect(() => {
    if (!orgId || filter !== 'movement') return;
    
    setLoading(true);

    if (getLocalAuthToken()) {
      let cancelled = false;

      fetch(`/api/organizations/${orgId}/inventory-movements`, {
        headers: getLocalApiHeaders()
      })
        .then(async (response) => {
          if (!response.ok) throw new Error('Unable to load inventory movements');
          return response.json();
        })
        .then((data) => {
          if (!cancelled) {
            setMovements(data);
            setLoading(false);
          }
        })
        .catch((error) => {
          if (!cancelled) {
            console.error('Local API error:', error);
            notify.error(error instanceof Error ? error.message : 'Unable to load inventory movements');
            setLoading(false);
          }
        });

      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'stock_movements'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMovements(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'stock_movements');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [orgId, filter]);

  const handleDirectMovement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (directMovement.items.length === 0) return;
    if (!getLocalAuthToken() && !auth.currentUser) return;

    try {
      setIsProcessing(true);
      const refId = `DM-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;

      if (getLocalAuthToken()) {
        for (const movementItem of directMovement.items) {
          if (!movementItem.productId || !movementItem.quantity) continue;
          const lot = movementItem.lotNumber
            ? lotNumbers.find(l => l.lotNumber === movementItem.lotNumber && l.productId === movementItem.productId)
            : null;
          const basePayload = {
            productId: movementItem.productId,
            quantity: Math.abs(movementItem.quantity),
            lotId: lot?.id,
            lotNumber: !lot && movementItem.lotNumber ? movementItem.lotNumber : undefined,
            expiryDate: movementItem.expiryDate || undefined,
            notes: directMovement.notes || undefined,
          };
          let endpoint: string;
          let payload: any;
          if (directMovement.type === 'transfer') {
            if (!directMovement.fromWarehouseId || !directMovement.toWarehouseId) {
              notify.error('Source and Target warehouses are required for transfer');
              setIsProcessing(false);
              return;
            }
            endpoint = `/api/organizations/${orgId}/inventory-transfers`;
            payload = { ...basePayload, sourceLocationId: directMovement.fromWarehouseId, destinationLocationId: directMovement.toWarehouseId };
          } else if (directMovement.type === 'sale') {
            if (!directMovement.fromWarehouseId) { notify.error('Warehouse is required'); setIsProcessing(false); return; }
            endpoint = `/api/organizations/${orgId}/inventory-issues`;
            payload = { ...basePayload, locationId: directMovement.fromWarehouseId };
          } else if (directMovement.type === 'purchase') {
            if (!directMovement.toWarehouseId) { notify.error('Warehouse is required'); setIsProcessing(false); return; }
            endpoint = `/api/organizations/${orgId}/inventory-receipts`;
            payload = { ...basePayload, locationId: directMovement.toWarehouseId };
          } else {
            if (!directMovement.toWarehouseId) { notify.error('Warehouse is required'); setIsProcessing(false); return; }
            endpoint = `/api/organizations/${orgId}/inventory-returns`;
            payload = { ...basePayload, locationId: directMovement.toWarehouseId };
          }
          const res = await fetch(endpoint, { method: 'POST', headers: getLocalApiHeaders(), body: JSON.stringify(payload) });
          if (!res.ok) { const err = await res.json().catch(() => null); throw new Error(err?.error || 'Could not record movement'); }
        }

        notify.confirm('Movement recorded successfully');
        setDirectMovement({
          type: 'sale',
          fromWarehouseId: '',
          toWarehouseId: '',
          reference: '',
          notes: '',
          items: [{ productId: '', quantity: 1, lotNumber: '', expiryDate: '' }]
        });
        setFilter('movement');
        setIsProcessing(false);
        return;
      }

      for (const movementItem of directMovement.items) {
        if (!movementItem.productId || !movementItem.quantity) continue;
        
        const itemRef = doc(db, 'items', movementItem.productId);
        const itemSnap = await getDoc(itemRef);
        
        if (!itemSnap.exists()) {
          notify.error('Product not found');
          continue;
        }

        const itemData = itemSnap.data() as Item;

        // Auto-create lot number record if it doesn't exist
        if (movementItem.lotNumber) {
          const hasLot = lotNumbers.find(l => l.lotNumber === movementItem.lotNumber && l.productId === movementItem.productId);
          if (!hasLot) {
            try {
              await addDoc(collection(db, 'lot_numbers'), {
                lotNumber: movementItem.lotNumber,
                productId: movementItem.productId,
                expiryDate: movementItem.expiryDate || '',
                orgId,
                createdAt: serverTimestamp()
              });
            } catch (err) {
              console.error("Error auto-creating lot:", err);
            }
          }
        }

        // 1. Update Inventory and Stock Records
        let updatedStockRecords = [...(itemData.stockRecords || [])];
        
        if (directMovement.type === 'transfer') {
          if (!directMovement.fromWarehouseId || !directMovement.toWarehouseId) {
            notify.error('Source and Target warehouses are required for transfer');
            setIsProcessing(false);
            return;
          }

          // Deduct from source
          const sourceIdx = updatedStockRecords.findIndex(r => r.warehouseId === directMovement.fromWarehouseId && r.lotNumber === movementItem.lotNumber);
          if (sourceIdx === -1 || updatedStockRecords[sourceIdx].quantity < movementItem.quantity) {
            if (!itemData.allowNegativeStock) {
              notify.error(`Insufficient stock for ${itemData.name} in source warehouse`);
              setIsProcessing(false);
              return;
            }
          }
          
          if (sourceIdx !== -1) {
            updatedStockRecords[sourceIdx].quantity -= movementItem.quantity;
          } else {
            updatedStockRecords.push({
              id: Math.random().toString(36).substr(2, 9),
              warehouseId: directMovement.fromWarehouseId,
              warehouseName: stores.find(s => s.id === directMovement.fromWarehouseId)?.name || 'Unknown',
              lotNumber: movementItem.lotNumber,
              expiryDate: movementItem.expiryDate,
              quantity: -movementItem.quantity,
              costPrice: itemData.costPrice || 0
            });
          }

          // Add to target
          const targetIdx = updatedStockRecords.findIndex(r => r.warehouseId === directMovement.toWarehouseId && r.lotNumber === movementItem.lotNumber);
          if (targetIdx !== -1) {
            updatedStockRecords[targetIdx].quantity += movementItem.quantity;
          } else {
            updatedStockRecords.push({
              id: Math.random().toString(36).substr(2, 9),
              warehouseId: directMovement.toWarehouseId,
              warehouseName: stores.find(s => s.id === directMovement.toWarehouseId)?.name || 'Unknown',
              lotNumber: movementItem.lotNumber,
              expiryDate: movementItem.expiryDate,
              quantity: movementItem.quantity,
              costPrice: itemData.costPrice || 0
            });
          }
        } else {
          // Sale, Purchase, or Return
          const qtyToApply = (directMovement.type === 'sale' || (directMovement.type === 'return' && movementItem.quantity < 0)) ? -movementItem.quantity : movementItem.quantity;
          const targetWarehouse = directMovement.type === 'sale' ? directMovement.fromWarehouseId : directMovement.toWarehouseId;
          
          if (!targetWarehouse) {
            notify.error('Warehouse is required');
            setIsProcessing(false);
            return;
          }

          const recIdx = updatedStockRecords.findIndex(r => r.warehouseId === targetWarehouse && r.lotNumber === movementItem.lotNumber);
          if (recIdx !== -1) {
            updatedStockRecords[recIdx].quantity += qtyToApply;
          } else {
            updatedStockRecords.push({
              id: Math.random().toString(36).substr(2, 9),
              warehouseId: targetWarehouse,
              warehouseName: stores.find(s => s.id === targetWarehouse)?.name || 'Unknown',
              lotNumber: movementItem.lotNumber,
              expiryDate: movementItem.expiryDate,
              quantity: qtyToApply,
              costPrice: itemData.costPrice || 0
            });
          }
        }

        const totalQty = updatedStockRecords.reduce((sum, r) => sum + (r.quantity || 0), 0);
        
        await updateDoc(itemRef, {
          stockRecords: updatedStockRecords,
          quantity: totalQty,
          updatedAt: serverTimestamp()
        });

        // 2. Create Stock Movement Record
        await addDoc(collection(db, 'stock_movements'), {
          orgId,
          itemId: movementItem.productId,
          productName: itemData.name,
          type: directMovement.type,
          quantity: movementItem.quantity,
          fromWarehouseId: directMovement.fromWarehouseId,
          toWarehouseId: directMovement.toWarehouseId,
          lotNumber: movementItem.lotNumber,
          expiryDate: movementItem.expiryDate,
          referenceNumber: directMovement.reference || refId,
          note: directMovement.notes,
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser.uid,
          createdByName: auth.currentUser.displayName || 'Anonymous',
          value: itemData.unitPrice * movementItem.quantity
        });
      }

      const movementRef = directMovement.reference || refId;
      await logHistory(orgId, 'Created' + (directMovement.type === 'transfer' ? ' Internal Transfer' : ' Stock Movement'), `${directMovement.type.toUpperCase()} movement ${movementRef} recorded with ${directMovement.items.length} items.`, auth.currentUser, movementRef, 'Movement');

      notify.confirm('Movement recorded successfully');
      setDirectMovement({
        type: 'sale',
        fromWarehouseId: '',
        toWarehouseId: '',
        reference: '',
        notes: '',
        items: [{ productId: '', quantity: 1, lotNumber: '', expiryDate: '' }]
      });
      setFilter('movement');
    } catch (error) {
      console.error(error);
      notify.error('Error recording movement');
    } finally {
      setIsProcessing(false);
    }
  };

  const exportInventoryReport = () => {
    const headers = ['Product', 'Warehouse', 'Lot #', 'Expiry', 'Quantity', 'Last Action By'];
    const records: any[] = [];
    items.forEach(item => {
      if (item.type !== 'product' || item.status === 'archived') return;
      const itemRecords = item.stockRecords || [];
      itemRecords.forEach(rec => {
        const matchesProduct = !reportFilters.productId || item.id === reportFilters.productId;
        const matchesWarehouse = !reportFilters.warehouseId || rec.warehouseId === reportFilters.warehouseId;
        const matchesLot = !reportFilters.lotNumber || rec.lotNumber?.toLowerCase().includes(reportFilters.lotNumber.toLowerCase());
        const matchesExpiryBefore = !reportFilters.expiryBefore || (rec.expiryDate && rec.expiryDate <= reportFilters.expiryBefore);
        if (matchesProduct && matchesWarehouse && matchesLot && matchesExpiryBefore) {
          records.push([
            item.name,
            rec.warehouseName,
            rec.lotNumber || '-',
            rec.expiryDate || '-',
            rec.quantity,
            item.actionLog?.[0]?.by || item.createdBy || 'System'
          ]);
        }
      });
    });
    
    const csvContent = [headers, ...records].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Inventory_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportMovementReport = () => {
    const headers = ['Ref #', 'Product', 'Type', 'From', 'To', 'Qty', 'Value', 'User / Action By', 'Date'];
    
    // Apply filters
    const filteredMovements = movements.filter(m => {
      let matches = false;
      if (m.createdAt && m.createdAt.toDate) {
        const d = m.createdAt.toDate().toISOString().split('T')[0];
        matches = d >= reportFilters.startDate && d <= reportFilters.endDate;
      } else {
        matches = true;
      }
      return matches && (!reportFilters.movementType || m.type === reportFilters.movementType);
    });

    const rows = filteredMovements.map(m => [
      m.referenceNumber,
      m.productName || items.find(i => i.id === m.itemId)?.name || 'Unknown',
      m.type,
      m.fromWarehouseId ? stores.find(s => s.id === m.fromWarehouseId)?.name : '-',
      m.toWarehouseId ? stores.find(s => s.id === m.toWarehouseId)?.name : '-',
      m.quantity,
      m.value || 0,
      m.createdByName || m.createdBy || 'System',
      m.createdAt?.toDate ? m.createdAt.toDate().toISOString() : ''
    ]);
    
    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Movement_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const announceDropdownOpen = useExclusiveDropdown('items', () => setActiveMenu(null));
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [taxRates, setTaxRates] = useState<any[]>([]);
  const [currency, setCurrency] = useState('USD');

  useEffect(() => {
    const handleQuickAction = (e: any) => {
      if (e.detail?.action === 'new-item') {
        setEditingItem(null);
        setNewItem({
          name: '',
          code: '',
          description: '',
          type: 'product',
          unitPrice: 0,
          costPrice: 0,
          taxRate: '0%',
          status: 'active'
        } as any);
        setIsModalOpen(true);
      }
    };
    window.addEventListener('QUICK_ACTION', handleQuickAction as EventListener);
    return () => window.removeEventListener('QUICK_ACTION', handleQuickAction as EventListener);
  }, []);

  useEffect(() => {
    if (!orgId) return;
    if (getLocalAuthToken()) {
      let cancelled = false;
      fetch(`/api/organizations/${orgId}/settings`, { headers: getLocalApiHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (!cancelled && data) setCurrency(getDefaultCurrency(data)); })
        .catch(err => { if (!cancelled) console.error('Settings load failed:', err); });
      return () => { cancelled = true; };
    }
    const unsub = onSnapshot(doc(db, 'settings', orgId), (doc) => {
      if (doc.exists()) {
        setCurrency(getDefaultCurrency(doc.data()));
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, `settings/${orgId}`);
    });
    return () => unsub();
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    if (getLocalAuthToken()) {
      let cancelled = false;
      Promise.all([
        fetch(`/api/organizations/${orgId}/uoms`, { headers: getLocalApiHeaders() }).then(r => r.ok ? r.json() : []),
        fetch(`/api/organizations/${orgId}/inventory-movements`, { headers: getLocalApiHeaders() }).then(r => r.ok ? r.json() : []),
      ]).then(([units, inventoryMovements]) => {
        if (cancelled) return;
        setUoms(Array.isArray(units) ? units : []);
        const ids = new Set<string>();
        if (Array.isArray(inventoryMovements)) {
          inventoryMovements.forEach(movement => {
            const id = movement?.itemId || movement?.item_id || movement?.productId || movement?.product_id;
            if (typeof id === 'string') ids.add(id);
          });
        }
        setMovementItemIds(ids);
      }).catch(error => {
        if (!cancelled) console.error('UOM or movement metadata load failed:', error);
      });
      return () => { cancelled = true; };
    }

    const unitsQuery = query(collection(db, 'uoms'), where('orgId', '==', orgId));
    const movementsQuery = query(collection(db, 'stock_movements'), where('orgId', '==', orgId), limit(500));
    const unsubscribeUnits = onSnapshot(unitsQuery, snapshot => {
      setUoms(snapshot.docs.map(unit => ({ id: unit.id, ...unit.data() } as UOM)));
    }, error => console.error('UOM metadata load failed:', error));
    const unsubscribeMovements = onSnapshot(movementsQuery, snapshot => {
      setMovementItemIds(new Set(snapshot.docs.map(movement => {
        const data = movement.data();
        return data.itemId || data.productId;
      }).filter((id): id is string => typeof id === 'string')));
    }, error => console.error('Movement metadata load failed:', error));
    return () => { unsubscribeUnits(); unsubscribeMovements(); };
  }, [orgId]);

  const [newItem, setNewItem] = useState<Partial<Item>>({
    code: '',
    name: '',
    description: '',
    unitPrice: 0,
    costPrice: 0,
    quantity: 0,
    type: 'product',
    status: 'active',
    categoryId: '',
    account: '',
    taxRate: '0%',
    enablePOS: false,
    trackInventory: false,
    allowNegativeStock: false,
    hasExpiry: false,
    hideFromSellScreen: false,
    imageUrl: '',
    tags: [],
    recipes: [],
    storeStock: {},
    hasVariants: false,
    variantOptions: [],
    variants: [],
    lots: [{ id: Math.random().toString(36).substr(2, 9), lotNumber: '', expiryDate: '' }],
    stockRecords: []
  });

  const isDuplicateCode = items.some(item => item.code.toLowerCase() === newItem.code?.toLowerCase());

    useEffect(() => {
      if (getLocalAuthToken()) return;
      async function testConnection() {
        try {
          await getDocFromServer(doc(db, 'test', 'connection'));
        } catch (error) {
          if(error instanceof Error && error.message.includes('the client is offline')) {
            console.error("Please check your Firebase configuration. ");
          }
        }
      }
      testConnection();
    }, []);

  useEffect(() => {
    if (!orgId) return;

    if (getLocalAuthToken()) {
      let cancelled = false;
      setLoading(true);

      fetch(`/api/organizations/${orgId}/items`, {
        headers: getLocalApiHeaders()
      })
        .then(async (response) => {
          if (!response.ok) throw new Error('Unable to load items');
          return response.json();
        })
        .then((data) => {
          if (!cancelled) {
            setItems(data as Item[]);
            setLoading(false);
          }
        })
        .catch((error) => {
          if (!cancelled) {
            console.error('Local API error:', error);
            notify.error(error instanceof Error ? error.message : 'Unable to load items');
            setLoading(false);
          }
        });

      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'items'),
      where('orgId', '==', orgId),
      orderBy('name', 'asc'),
      limit(200)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Item));
      setItems(docs);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'items');
    });

    return () => unsubscribe();
  }, [orgId]);

  useEffect(() => {
    if (initialItemId && items.length > 0) {
      const item = items.find(i => i.id === initialItemId);
      if (item) {
        setViewingItem(item);
        if (onClearInitial) onClearInitial();
      }
    }
  }, [initialItemId, items]);

  useEffect(() => {
    if (!orgId) return;

    if (getLocalAuthToken()) {
      let cancelled = false;
      fetch(`/api/organizations/${orgId}/tax-rates`, { headers: getLocalApiHeaders() })
        .then(r => r.json())
        .then(data => { if (!cancelled) setTaxRates(Array.isArray(data) ? data : []); })
        .catch(err => { if (!cancelled) console.error('Tax rates load failed:', err); });
      return () => { cancelled = true; };
    }

    const taxRatesQ = query(
      collection(db, 'tax_rates'),
      where('orgId', '==', orgId)
    );
    const unsubTaxRates = onSnapshot(taxRatesQ, (snapshot) => {
      setTaxRates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tax_rates');
    });

    return () => {
      unsubTaxRates();
    };
  }, [orgId]);

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check for duplicate lot numbers
    if (newItem.lots) {
      const lotNumbers = (newItem.lots || []).map(l => l.lotNumber.trim()).filter(n => n !== '');
      const uniqueLots = new Set(lotNumbers);
      if (lotNumbers.length !== uniqueLots.size) {
        notify.error(language === 'ar' ? 'غير مسموح بتكرار أرقام اللوط لنفس المنتج' : "Duplicate lot numbers are not allowed for the same product.");
        return;
      }
    }

    const totalQtyFromRecords = (newItem.stockRecords || []).reduce((sum, rec) => sum + (rec.quantity || 0), 0);

    const path = 'items';
    try {
      if (getLocalAuthToken()) {
        const itemPayload = {
          ...newItem,
          imageUrl: newItem.imageUrl || '',
          quantity: totalQtyFromRecords || newItem.quantity || 0
        };
        const response = await fetch(`/api/organizations/${orgId}/items`, {
          method: 'POST',
          headers: getLocalApiHeaders(),
          body: JSON.stringify(itemPayload)
        });
        if (!response.ok) throw new Error('Unable to create item');

        const createdItem = await response.json() as Item;
        setItems(currentItems => [...currentItems, createdItem].sort((a, b) => a.name.localeCompare(b.name)));
        setIsModalOpen(false);
        setNewItem({
          code: '',
          name: '',
          description: '',
          unitPrice: 0,
          costPrice: 0,
          type: 'product',
          status: 'active',
          account: '',
          taxRate: '0%',
          enablePOS: false,
          trackInventory: false,
          allowNegativeStock: false,
          hasExpiry: false,
          hideFromSellScreen: false,
          imageUrl: '',
          tags: [],
          recipes: [],
          storeStock: {},
          hasVariants: false,
          variantOptions: [],
          variants: [],
          lots: [{ id: Math.random().toString(36).substr(2, 9), lotNumber: '', expiryDate: '' }]
        });
        notify.confirm('Item created successfully');
        return;
      }

      if (!auth.currentUser) return;

      const actionLog: ActionLogEntry[] = [{
        action: 'Created',
        by: auth.currentUser.displayName || 'Unknown',
        byEmail: auth.currentUser.email || 'Unknown',
        at: new Date()
      }];

      const docRef = await addDoc(collection(db, path), {
        ...newItem,
        quantity: totalQtyFromRecords || newItem.quantity || 0,
        orgId,
        createdBy: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        actionLog
      });

      // Save variants as normal products
      if (newItem.hasVariants && newItem.variants && newItem.variants.length > 0) {
        const variantsToSave = newItem.variants.filter(v => v.save !== false);
        for (const variant of variantsToSave) {
          await addDoc(collection(db, path), {
            code: variant.sku,
            name: `${newItem.name} - ${variant.name}`,
            description: newItem.description || '',
            unitPrice: variant.unitPrice,
            costPrice: variant.costPrice,
            quantity: variant.quantity,
            type: newItem.type,
            status: 'active',
            parentId: docRef.id,
            isVariant: true,
            barcode: variant.barcode,
            enablePOS: newItem.enablePOS,
            allowNegativeStock: newItem.allowNegativeStock,
            hasExpiry: newItem.hasExpiry,
            hideFromSellScreen: newItem.hideFromSellScreen,
            imageUrl: newItem.imageUrl,
            tags: newItem.tags,
            orgId,
            createdBy: auth.currentUser.uid,
            createdAt: serverTimestamp(),
            actionLog: [{
              action: 'Created as Variant',
              details: `Variant of ${newItem.name}`,
              by: auth.currentUser.displayName || 'Unknown',
              byEmail: auth.currentUser.email || 'Unknown',
              at: new Date()
            }]
          });
        }
      }

      setIsModalOpen(false);
      setNewItem({
        code: '',
        name: '',
        description: '',
        unitPrice: 0,
        costPrice: 0,
        type: 'product',
        status: 'active',
        account: '',
        taxRate: '0%',
        enablePOS: false,
        trackInventory: false,
        allowNegativeStock: false,
        hasExpiry: false,
        hideFromSellScreen: false,
        imageUrl: '',
        tags: [],
        recipes: [],
        storeStock: {},
        hasVariants: false,
        variantOptions: [],
        variants: [],
        lots: [{ id: Math.random().toString(36).substr(2, 9), lotNumber: '', expiryDate: '' }]
      });
      notify.confirm('Item created successfully');
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, path);
    }
  };

  const handleUpdateItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;

    // Check for duplicate lot numbers
    if (editingItem.lots) {
      const lotNumbers = (editingItem.lots || []).map(l => l.lotNumber.trim()).filter(n => n !== '');
      const uniqueLots = new Set(lotNumbers);
      if (lotNumbers.length !== uniqueLots.size) {
        notify.error(language === 'ar' ? 'غير مسموح بتكرار أرقام اللوط لنفس المنتج' : "Duplicate lot numbers are not allowed for the same product.");
        return;
      }
    }

    const totalQtyFromRecords = (editingItem.stockRecords || []).reduce((sum, rec) => sum + (rec.quantity || 0), 0);

    const path = `items/${editingItem.id}`;
    try {
      if (getLocalAuthToken()) {
        const { id, actionLog, ...updateData } = editingItem;
        const itemPayload = {
          ...updateData,
          imageUrl: editingItem.imageUrl || '',
          quantity: totalQtyFromRecords || editingItem.quantity || 0
        };
        const response = await fetch(`/api/organizations/${orgId}/items/${editingItem.id}`, {
          method: 'PATCH',
          headers: getLocalApiHeaders(),
          body: JSON.stringify(itemPayload)
        });
        if (!response.ok) throw new Error('Unable to update item');

        const updatedItem = await response.json() as Item;
        setItems(currentItems => currentItems.map(item => item.id === updatedItem.id ? updatedItem : item));
        setEditingItem(null);
        notify.confirm('Item updated successfully');
        return;
      }

      if (!auth.currentUser) return;

      const itemRef = doc(db, 'items', editingItem.id);
      const originalItem = items.find(i => i.id === editingItem.id);
      const { id, actionLog, ...updateData } = editingItem;
      
      const changes: string[] = [];
      if (originalItem) {
        if (originalItem.code !== editingItem.code) changes.push('Code');
        if (originalItem.name !== editingItem.name) changes.push('Name');
        if (originalItem.description !== editingItem.description) changes.push('Description');
        if (originalItem.unitPrice !== editingItem.unitPrice) changes.push('Unit Price');
        if (originalItem.costPrice !== editingItem.costPrice) changes.push('Cost Price');
        if (originalItem.type !== editingItem.type) changes.push('Type');
        if (originalItem.account !== editingItem.account) changes.push('Account');
        if (originalItem.taxRate !== editingItem.taxRate) changes.push('Tax Rate');
        if (originalItem.enablePOS !== editingItem.enablePOS) changes.push('POS Status');
        if (originalItem.allowNegativeStock !== editingItem.allowNegativeStock) changes.push('Negative Stock Setting');
        if (originalItem.hasExpiry !== editingItem.hasExpiry) changes.push('Expiry Tracking Status');
        if (originalItem.hideFromSellScreen !== editingItem.hideFromSellScreen) changes.push('Sell Screen Visibility');
        if (JSON.stringify(originalItem.recipes) !== JSON.stringify(editingItem.recipes)) changes.push('Recipes');
        if (originalItem.hasVariants !== editingItem.hasVariants) changes.push('Variant Management');
        if (JSON.stringify(originalItem.variants) !== JSON.stringify(editingItem.variants)) changes.push('Variants');
        if (JSON.stringify(originalItem.storeAvailability) !== JSON.stringify(editingItem.storeAvailability)) changes.push('Store Availability');
        if (JSON.stringify(originalItem.stockRecords) !== JSON.stringify(editingItem.stockRecords)) changes.push('Stock Records');
      }

      const newLogEntry: ActionLogEntry = {
        action: 'Updated',
        details: changes.length > 0 ? `Changed: ${changes.join(', ')}` : 'No fields changed',
        by: auth.currentUser.displayName || 'Unknown',
        byEmail: auth.currentUser.email || 'Unknown',
        at: new Date()
      };

      await updateDoc(itemRef, {
        ...updateData,
        quantity: totalQtyFromRecords || editingItem.quantity || 0,
        updatedAt: serverTimestamp(),
        actionLog: [...(actionLog || []), newLogEntry]
      });

      // Save variants as normal products
      if (editingItem.hasVariants && editingItem.variants && editingItem.variants.length > 0) {
        const itemsPath = 'items';
        const variantsToSave = editingItem.variants.filter(v => v.save !== false);
        for (const variant of variantsToSave) {
          // Check if variant already exists (by SKU and parentId)
          const existingVariant = items.find(i => i.code === variant.sku && i.parentId === editingItem.id);
          
          if (!existingVariant) {
            await addDoc(collection(db, itemsPath), {
              code: variant.sku,
              name: `${editingItem.name} - ${variant.name}`,
              description: editingItem.description || '',
              unitPrice: variant.unitPrice,
              costPrice: variant.costPrice,
              quantity: variant.quantity,
              type: editingItem.type,
              status: 'active',
              parentId: editingItem.id,
              isVariant: true,
              barcode: variant.barcode,
              enablePOS: editingItem.enablePOS,
              allowNegativeStock: editingItem.allowNegativeStock,
              hasExpiry: editingItem.hasExpiry,
              hideFromSellScreen: editingItem.hideFromSellScreen,
              imageUrl: editingItem.imageUrl,
              tags: editingItem.tags,
              createdBy: auth.currentUser.uid,
              createdAt: serverTimestamp(),
              actionLog: [{
                action: 'Created as Variant',
                details: `Variant of ${editingItem.name}`,
                by: auth.currentUser.displayName || 'Unknown',
                byEmail: auth.currentUser.email || 'Unknown',
                at: new Date()
              }]
            });
          } else {
            // Update existing variant
            const variantRef = doc(db, itemsPath, existingVariant.id);
            await updateDoc(variantRef, {
              name: `${editingItem.name} - ${variant.name}`,
              unitPrice: variant.unitPrice,
              costPrice: variant.costPrice,
              barcode: variant.barcode,
              status: editingItem.status,
            });
          }
        }
      }

      setEditingItem(null);
      notify.confirm('Item updated successfully');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, path);
    }
  };

  const handleScrapSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!getLocalAuthToken() && !auth.currentUser) return;
    if (!scrapData.productId || scrapData.quantity <= 0) {
      notify.error("Please fill in all required fields correctly.");
      return;
    }

    try {
      setIsProcessing(true);

      if (getLocalAuthToken()) {
        // Use inventory-adjustments endpoint for scrap (negative adjustment)
        const lot = scrapData.lotNumber
          ? lotNumbers.find(l => l.lotNumber === scrapData.lotNumber && l.productId === scrapData.productId)
          : null;
        const scrRes = await fetch(`/api/organizations/${orgId}/inventory-adjustments`, {
          method: 'POST', headers: getLocalApiHeaders(),
          body: JSON.stringify({
            productId: scrapData.productId, locationId: scrapData.warehouseId,
            quantity: -scrapData.quantity, lotId: lot?.id,
            reason: `Scrap/Salvage: ${scrapData.reason || 'No reason provided'}`,
          }),
        });
        if (!scrRes.ok) { const e = await scrRes.json().catch(() => null); throw new Error(e?.error || 'Could not process scrap'); }
        if (scrapData.action === 'salvage' && scrapData.targetProductId) {
          const salvRes = await fetch(`/api/organizations/${orgId}/inventory-receipts`, {
            method: 'POST', headers: getLocalApiHeaders(),
            body: JSON.stringify({
              productId: scrapData.targetProductId, locationId: scrapData.warehouseId,
              quantity: scrapData.quantity, lotId: lot?.id,
              notes: `Salvaged from item ${scrapData.productId}`,
            }),
          });
          if (!salvRes.ok) console.warn('Salvage receipt failed (non-fatal)');
        }
        notify.confirm(scrapData.action === 'write-off' ? 'Item written off successfully' : 'Item salvaged successfully');
        setScrapData({ productId: '', warehouseId: '', quantity: 1, action: 'write-off', targetProductId: '', reason: '', lotNumber: '' });
        setIsProcessing(false);
        return;
      }

      const itemRef = doc(db, 'items', scrapData.productId);
      const itemSnap = await getDoc(itemRef);
      if (!itemSnap.exists()) {
        notify.error("Source product not found");
        return;
      }
      const itemData = itemSnap.data() as Item;
      
      // Update inventory for source item
      let updatedStockRecords = [...(itemData.stockRecords || [])];
      const recIdx = updatedStockRecords.findIndex(r => r.warehouseId === scrapData.warehouseId && r.lotNumber === scrapData.lotNumber);
      
      if (recIdx === -1 || updatedStockRecords[recIdx].quantity < scrapData.quantity) {
        if (!itemData.allowNegativeStock) {
          notify.error("Insufficient stock for this operation");
          setIsProcessing(false);
          return;
        }
      }

      if (recIdx !== -1) {
        updatedStockRecords[recIdx].quantity -= scrapData.quantity;
      } else {
        updatedStockRecords.push({
          id: Math.random().toString(36).substr(2, 9),
          warehouseId: scrapData.warehouseId,
          warehouseName: stores.find(s => s.id === scrapData.warehouseId)?.name || 'Unknown',
          lotNumber: scrapData.lotNumber,
          quantity: -scrapData.quantity,
          costPrice: itemData.costPrice || 0
        });
      }

      await updateDoc(itemRef, {
        stockRecords: updatedStockRecords,
        quantity: updatedStockRecords.reduce((sum, r) => sum + (r.quantity || 0), 0),
        updatedAt: serverTimestamp()
      });

      // Log movement for source item
      await addDoc(collection(db, 'stock_movements'), {
        orgId,
        itemId: scrapData.productId,
        productName: itemData.name,
        type: scrapData.action === 'write-off' ? 'scrap' : 'salvage-out',
        quantity: scrapData.quantity,
        fromWarehouseId: scrapData.warehouseId,
        lotNumber: scrapData.lotNumber,
        referenceNumber: `SCRP-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
        note: `Scrap/Salvage: ${scrapData.reason || 'No reason provided'}`,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser.uid,
        createdByName: auth.currentUser.displayName || 'Anonymous',
        value: -(itemData.costPrice || 0) * scrapData.quantity
      });

      // If salvage, update target product
      if (scrapData.action === 'salvage' && scrapData.targetProductId) {
        const targetRef = doc(db, 'items', scrapData.targetProductId);
        const targetSnap = await getDoc(targetRef);
        if (targetSnap.exists()) {
          const targetData = targetSnap.data() as Item;
          let targetStockRecords = [...(targetData.stockRecords || [])];
          const tIdx = targetStockRecords.findIndex(r => r.warehouseId === scrapData.warehouseId && r.lotNumber === scrapData.lotNumber);
          
          if (tIdx !== -1) {
            targetStockRecords[tIdx].quantity += scrapData.quantity;
          } else {
            targetStockRecords.push({
              id: Math.random().toString(36).substr(2, 9),
              warehouseId: scrapData.warehouseId,
              warehouseName: stores.find(s => s.id === scrapData.warehouseId)?.name || 'Unknown',
              lotNumber: scrapData.lotNumber,
              quantity: scrapData.quantity,
              costPrice: targetData.costPrice || 0
            });
          }

          await updateDoc(targetRef, {
            stockRecords: targetStockRecords,
            quantity: targetStockRecords.reduce((sum, r) => sum + (r.quantity || 0), 0),
            updatedAt: serverTimestamp()
          });

          // Log movement for target item
          await addDoc(collection(db, 'stock_movements'), {
            orgId,
            itemId: scrapData.targetProductId,
            productName: targetData.name,
            type: 'salvage-in',
            quantity: scrapData.quantity,
            toWarehouseId: scrapData.warehouseId,
            lotNumber: scrapData.lotNumber,
            referenceNumber: `SLVG-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
            note: `Salvaged from ${itemData.name}`,
            createdAt: serverTimestamp(),
            createdBy: auth.currentUser.uid,
            createdByName: auth.currentUser.displayName || 'Anonymous',
            value: (targetData.costPrice || 0) * scrapData.quantity
          });
        }
      }

      notify.confirm(scrapData.action === 'write-off' ? "Item written off successfully" : "Item salvaged successfully");
      setScrapData({
        productId: '',
        warehouseId: '',
        quantity: 1,
        action: 'write-off',
        targetProductId: '',
        reason: '',
        lotNumber: ''
      });
    } catch (error) {
      console.error(error);
      notify.error("Error processing scrap operation");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleArchiveItem = async (id: string, currentStatus: 'active' | 'archived') => {
    const path = `items/${id}`;
    
    try {
      if (getLocalAuthToken()) {
        const response = await fetch(`/api/organizations/${orgId}/items/${id}`, {
          method: 'DELETE',
          headers: getLocalApiHeaders()
        });
        if (!response.ok) throw new Error('Unable to delete item');

        setItems(currentItems => currentItems.filter(item => item.id !== id));
        setActiveMenu(null);
        notify.confirm('Item deleted successfully');
        return;
      }

      if (!auth.currentUser) return;

      const itemRef = doc(db, 'items', id);
      const item = items.find(i => i.id === id);
      
      const newLogEntry: ActionLogEntry = {
        action: currentStatus === 'active' ? 'Archived' : 'Restored',
        by: auth.currentUser.displayName || 'Unknown',
        byEmail: auth.currentUser.email || 'Unknown',
        at: new Date()
      };

      await updateDoc(itemRef, {
        status: currentStatus === 'active' ? 'archived' : 'active',
        updatedAt: serverTimestamp(),
        actionLog: [...(item?.actionLog || []), newLogEntry]
      });
      setActiveMenu(null);
      notify.confirm(`Item ${currentStatus === 'active' ? 'archived' : 'restored'} successfully`);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, path);
    }
  };

  const filteredItems = items.filter(item => {
    const matchesStatus = filter === 'archived' ? item.status === 'archived' : (statusFilter === 'all' ? item.status !== 'archived' : item.status === statusFilter);
    const matchesType = (filter === 'all' || filter === 'archived' || item.type === filter) && (itemTypeFilter === 'all' || item.type === itemTypeFilter);
    
    // Search logic enhancement (include tags if needed)
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         item.code.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Amount Filters
    const matchesMinPrice = !minPrice || (item.salesPrice || 0) >= parseFloat(minPrice);
    const matchesMaxPrice = !maxPrice || (item.salesPrice || 0) <= parseFloat(maxPrice);
    
    // Stock Filters
    const totalQty = item.quantity || 0;
    const matchesMinStock = !minStock || totalQty >= parseFloat(minStock);
    const matchesMaxStock = !maxStock || totalQty <= parseFloat(maxStock);

    return matchesStatus && matchesType && matchesSearch && matchesMinPrice && matchesMaxPrice && matchesMinStock && matchesMaxStock;
  });

  const renderUomSelectors = (
    value: Partial<Item>,
    update: (next: Partial<Item>) => void,
    stockLocked = false,
  ) => {
    const stockId = getProductUomId(value, 'stock');
    const compatible = compatibleUoms(uoms, stockId);
    const selectClass = "w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none text-sm";
    const changeStock = (id: string) => {
      const stock = resolveUom(uoms, id);
      let next = withProductUom(value, 'stock', stock);
      const allowedIds = new Set(compatibleUoms(uoms, id).map(unit => unit.id));
      if (!allowedIds.has(getProductUomId(next, 'purchase'))) next = withProductUom(next, 'purchase', stock);
      if (!allowedIds.has(getProductUomId(next, 'sales'))) next = withProductUom(next, 'sales', stock);
      update(next);
    };
    return (
      <div className="col-span-2 p-4 bg-white/30 border border-[#141414]/10 rounded-xl space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest">Units of Measure</p>
            <p className="text-[10px] opacity-50 mt-1">Purchase and sales units must use the stock unit category.</p>
          </div>
          <span className="px-2 py-1 rounded-full bg-[#141414] text-white text-[9px] font-bold uppercase tracking-wider">
            {value.type === 'service' ? 'Service • non-stock' : value.trackInventory ? 'Product • inventory tracked' : 'Product • inventory not tracked'}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="space-y-1 text-[10px] font-bold uppercase tracking-widest opacity-70">
            Stock UOM
            <select value={stockId} disabled={stockLocked} onChange={event => changeStock(event.target.value)} className={`${selectClass} disabled:cursor-not-allowed disabled:opacity-50`}>
              <option value="">Select unit</option>
              {compatibleUoms(uoms).map(unit => <option key={unit.id} value={unit.id}>{unit.name} ({unit.category})</option>)}
            </select>
          </label>
          {(['purchase', 'sales'] as const).map(role => (
            <label key={role} className="space-y-1 text-[10px] font-bold uppercase tracking-widest opacity-70">
              {role} UOM
              <select
                value={getProductUomId(value, role)}
                disabled={!stockId}
                onChange={event => update(withProductUom(value, role, resolveUom(uoms, event.target.value)))}
                className={`${selectClass} disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <option value="">Select unit</option>
                {compatible.map(unit => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
              </select>
            </label>
          ))}
        </div>
        {stockLocked && <p className="text-[10px] font-bold text-amber-700">Stock UOM is locked because inventory movements exist for this product.</p>}
      </div>
    );
  };

  const recipeUomOptions = (itemId: string, currentUnit?: string) => {
    const component = items.find(item => item.id === itemId);
    const stockId = getProductUomId(component, 'stock');
    const options = compatibleUoms(uoms, stockId);
    const legacy = currentUnit && !resolveUom(uoms, currentUnit) ? currentUnit : '';
    return { options, legacy };
  };

  const generateVariants = (options: VariantOption[], basePrice: number, baseCost: number, baseCode: string) => {
    if (options.length === 0) return [];
    
    // Filter out options with no values
    const validOptions = options.filter(o => o.values.length > 0);
    if (validOptions.length === 0) return [];

    const combinations = (acc: any[], current: VariantOption): any[] => {
      if (acc.length === 0) return current.values.map(v => ({ [current.name]: v }));
      const result: any[] = [];
      acc.forEach(a => {
        current.values.forEach(v => {
          result.push({ ...a, [current.name]: v });
        });
      });
      return result;
    };

    const allCombinations = validOptions.reduce(combinations, []);

    return allCombinations.map((combo, idx) => {
      const name = Object.values(combo).join(' / ');
      return {
        id: Math.random().toString(36).substr(2, 9),
        name,
        sku: `${baseCode}-${idx + 1}`,
        options: combo,
        costPrice: baseCost,
        unitPrice: basePrice,
        barcode: '',
        quantity: 0,
        save: true
      };
    });
  };

  if (viewingItem) {
    return (
      <div
        className="flex min-h-0 flex-col gap-6 lg:flex-row lg:items-start"
        dir={language === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="min-w-0 flex-1">
          <ItemProfileView
            item={viewingItem}
            currency={currency}
            language={language}
            onBack={() => setViewingItem(null)}
            onEdit={() => {
              setEditingItem(viewingItem);
              setViewingItem(null);
            }}
          />
        </div>
        <DocumentChatterPanel
          orgId={orgId}
          entityId={viewingItem.id}
          entityType="Product"
          locale={language === 'ar' ? 'ar' : 'en'}
          className="lg:sticky lg:top-6 lg:shrink-0"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isInventoryMode && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Items</h1>
            <p className="text-sm opacity-50 italic serif mt-1">Manage your products and services</p>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setIsImportModalOpen(true)}
              className="flex items-center gap-2 bg-white border border-[#141414] text-[#141414] px-6 py-3 rounded-full hover:bg-[#141414]/5 transition-all font-bold"
            >
              <Upload className="w-4 h-4" />
              <span>Import</span>
            </button>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
            >
              <Plus className="w-4 h-4" />
              <span>New Item</span>
            </button>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      {!isInventoryMode && !isReportsMode && (
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/50 border border-[#141414] p-4 rounded-2xl">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto custom-scrollbar">
            {(['product', 'service'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  "px-4 py-2 rounded-full text-xs font-bold capitalize transition-all whitespace-nowrap",
                  filter === f ? "bg-[#141414] text-[#E4E3E0]" : "hover:bg-[#141414]/5 opacity-60"
                )}
              >
                {`${f}s`}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full flex-1 md:w-64">
              <Search className="w-4 h-4 opacity-50" />
              <input 
                type="text" 
                placeholder={language === 'ar' ? 'بحث عن أصناف...' : 'Search items...'} 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-none outline-none text-sm w-full"
              />
            </div>
            <button 
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="text-xs font-bold underline opacity-60 hover:opacity-100 whitespace-nowrap"
            >
              {showAdvancedFilters ? 'Less filters' : 'More filters'}
            </button>
          </div>
        </div>
      )}

      {showAdvancedFilters && !isInventoryMode && !isReportsMode && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white/50 border border-[#141414] p-6 rounded-2xl animate-in slide-in-from-top-2 mb-4 mt-[-10px]">
            <SearchableSelect 
              label={language === 'ar' ? 'النوع' : 'Type'}
              value={itemTypeFilter}
              onChange={e => setItemTypeFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'product', label: 'Product' },
                { value: 'service', label: 'Service' }
              ]}
              containerClassName="space-y-1.5"
            />
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">{language === 'ar' ? 'نطاق السعر' : 'Price Range'}</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={minPrice} onChange={e => setMinPrice(e.target.value)} placeholder="Min" className="w-full bg-white border border-[#141414]/10 rounded-xl px-3 py-2 text-xs font-bold outline-none" />
              <input type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} placeholder="Max" className="w-full bg-white border border-[#141414]/10 rounded-xl px-3 py-2 text-xs font-bold outline-none" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold opacity-40 tracking-widest">{language === 'ar' ? 'مستوى المخزون' : 'Stock Level'}</label>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={minStock} onChange={e => setMinStock(e.target.value)} placeholder="Min" className="w-full bg-white border border-[#141414]/10 rounded-xl px-3 py-2 text-xs font-bold outline-none" />
              <input type="number" value={maxStock} onChange={e => setMaxStock(e.target.value)} placeholder="Max" className="w-full bg-white border border-[#141414]/10 rounded-xl px-3 py-2 text-xs font-bold outline-none" />
            </div>
          </div>
            <SearchableSelect 
              label={language === 'ar' ? 'الحالة' : 'Status'}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'active', label: 'Active' },
                { value: 'draft', label: 'Draft' },
                { value: 'archived', label: 'Archived' }
              ]}
              containerClassName="space-y-1.5 flex-1"
            />
            <div className="flex items-end mb-1">
              <button 
                onClick={() => {
                  setItemTypeFilter('all');
                  setMinPrice('');
                  setMaxPrice('');
                  setMinStock('');
                  setMaxStock('');
                  setStatusFilter('all');
                  setSearchQuery('');
                }}
                className="px-4 py-2 bg-red-50 text-red-500 rounded-xl text-[10px] font-bold uppercase tracking-wider hover:bg-red-100 transition-colors h-[34px]"
              >
                Reset
              </button>
            </div>
        </div>
      )}

      {/* Inventory Sub-Tabs (Rendering content ONLY, no header/sub-tabs bar as they are in the parent) */}
      {(filter === 'inventory' || filter === 'reporting' || isInventoryMode || isReportsMode) && (
        <div className="space-y-6 animate-in fade-in duration-500">
          {!isInventoryMode && !isReportsMode && (
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold uppercase tracking-widest flex items-center gap-2">
                <Settings2 className="w-5 h-5" />
                Inventory Manager
              </h2>
            </div>
          )}
          {isReportsMode && (
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold uppercase tracking-widest flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Inventory Reports
              </h2>
            </div>
          )}

          <div className="bg-white/50 border border-[#141414] rounded-3xl overflow-hidden shadow-sm">
            {!isInventoryMode && !isReportsMode && (
              <div className="flex border-b border-[#141414] overflow-x-auto custom-scrollbar">
                {(['all', 'categories', 'warehouses', 'lots', 'scrap'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setInventorySubTab(tab)}
                    className={cn(
                      "px-6 py-4 text-[10px] font-bold uppercase tracking-widest transition-all shrink-0 whitespace-nowrap",
                      inventorySubTab === tab ? "bg-[#141414] text-[#E4E3E0]" : "hover:bg-[#141414]/5 opacity-60"
                    )}
                  >
                    {tab === 'all' ? 'All Items' : tab === 'categories' ? 'Categories' : tab === 'warehouses' ? 'Warehouses' : tab === 'lots' ? 'Lot Numbers' : 'Scrap / Salvage'}
                  </button>
                ))}
              </div>
            )}
            
            {isReportsMode && (
              <div className="flex border-b border-[#141414] overflow-x-auto custom-scrollbar">
                {(['movement', 'reporting', 'movement-by-delivery', 'direct-delivery'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setInventorySubTab(tab)}
                    className={cn(
                      "px-6 py-4 text-[10px] font-bold uppercase tracking-widest transition-all shrink-0 whitespace-nowrap",
                      inventorySubTab === tab ? "bg-[#141414] text-[#E4E3E0]" : "hover:bg-[#141414]/5 opacity-60"
                    )}
                  >
                    {tab === 'movement' ? 'Product Deliveries' : tab === 'reporting' ? 'Inventory by Lot/Warehouse' : tab === 'movement-by-delivery' ? 'Transfer History' : 'Operations (Transfers)'}
                  </button>
                ))}
              </div>
            )}
            {inventorySubTab === 'all' && (
              <div className="p-6 space-y-6">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  <div>
                    <h3 className="font-bold flex items-center gap-2">
                      <Package className="w-5 h-5 opacity-40" />
                      Products & Services
                    </h3>
                  </div>
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-xl flex-1 md:w-64 max-w-sm">
                      <Search className="w-4 h-4 opacity-50" />
                      <input 
                        type="text" 
                        placeholder="Search stock..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-transparent border-none outline-none text-xs w-full"
                      />
                    </div>
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-2 rounded-full hover:opacity-90 transition-opacity font-bold text-xs whitespace-nowrap"
                    >
                      <Plus className="w-3 h-3" />
                      <span>New Item</span>
                    </button>
                  </div>
                </div>

                <div className="bg-white border border-[#141414] rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-[#141414] bg-[#141414]/5">
                          <th className="p-4 text-[10px] font-bold uppercase tracking-wider opacity-60">Product / Service</th>
                          <th className="p-4 text-[10px] font-bold uppercase tracking-wider opacity-60">SKU / Code</th>
                          <th className="p-4 text-[10px] font-bold uppercase tracking-wider opacity-60">Type</th>
                          <th className="p-4 text-[10px] font-bold uppercase tracking-wider opacity-60">Category</th>
                          <th className="p-4 text-[10px] font-bold uppercase tracking-wider opacity-60">On Hand</th>
                          <th className="p-4 text-[10px] font-bold uppercase tracking-wider opacity-60">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {items.filter(i => i.status === 'active' && (i.name.toLowerCase().includes(searchQuery.toLowerCase()) || i.code.toLowerCase().includes(searchQuery.toLowerCase()))).length === 0 ? (
                          <tr><td colSpan={6} className="p-12 text-center opacity-40 italic">No items found.</td></tr>
                        ) : (
                          items.filter(i => i.status === 'active' && (i.name.toLowerCase().includes(searchQuery.toLowerCase()) || i.code.toLowerCase().includes(searchQuery.toLowerCase()))).slice(0, 100).map(item => (
                            <tr key={item.id} className="border-b border-[#141414]/10 hover:bg-[#141414]/5 transition-all group">
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <div className={cn(
                                    "w-8 h-8 rounded text-white flex items-center justify-center font-bold text-[10px]",
                                    item.type === 'service' ? "bg-blue-600" : "bg-[#141414]"
                                  )}>
                                    {item.name.substring(0, 2).toUpperCase()}
                                  </div>
                                  <span className="font-bold text-xs">{item.name}</span>
                                </div>
                              </td>
                              <td className="p-4 font-mono text-[10px] opacity-60 uppercase">{item.code}</td>
                              <td className="p-4">
                                <span className={cn(
                                  "text-[10px] font-bold uppercase px-2 py-0.5 rounded",
                                  item.type === 'service' ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"
                                )}>
                                  {item.type}
                                </span>
                              </td>
                              <td className="p-4 text-[10px] opacity-60 uppercase tracking-tight">{categories.find(c => c.id === item.categoryId)?.name || 'Uncategorized'}</td>
                              <td className="p-4">
                                {item.type === 'product' ? (
                                  <span className={cn(
                                    "font-mono font-bold text-xs px-2 py-0.5 rounded",
                                    (item.quantity || 0) <= 0 ? "bg-red-100 text-red-600" : (item.quantity || 0) < 10 ? "bg-yellow-100 text-yellow-600" : "bg-green-100 text-green-600"
                                  )}>
                                    {item.quantity || 0}
                                  </span>
                                ) : (
                                  <span className="text-[10px] opacity-30 italic">N/A</span>
                                )}
                              </td>
                              <td className="p-4 flex items-center gap-2">
                                <button 
                                  onClick={() => setViewingItem(item)}
                                  className="text-[10px] font-bold uppercase tracking-widest opacity-40 hover:opacity-100 underline decoration-dotted"
                                >
                                  Details
                                </button>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingItem(item);
                                  }}
                                  className="p-1 hover:bg-black/5 rounded-lg transition-colors"
                                >
                                  <Edit2 className="w-3.5 h-3.5 opacity-40 hover:opacity-100" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {inventorySubTab === 'categories' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold flex items-center gap-2">
                    <FolderTree className="w-5 h-5 opacity-40" />
                    Product Categories
                  </h3>
                  <button 
                    onClick={() => {
                      setEditingCategory(null);
                      setCategoryFormData({ name: '', description: '', status: 'active' });
                      setIsCategoryModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-xl hover:opacity-90 transition-all text-xs font-bold"
                  >
                    <Plus className="w-4 h-4" />
                    <span>New Category</span>
                  </button>
                </div>
                
                <div className="grid gap-4">
                  {categories.length === 0 ? (
                    <div className="p-12 text-center bg-white border border-[#141414]/10 rounded-2xl opacity-40 italic serif">
                      No categories created yet.
                    </div>
                  ) : (
                    categories.map(cat => {
                      const catItems = items.filter(i => i.categoryId === cat.id);
                      return (
                        <div key={cat.id} className="bg-white border border-[#141414] rounded-2xl overflow-hidden shadow-sm">
                          <div className="bg-[#141414]/5 p-4 flex items-center justify-between border-b border-[#141414]/10">
                            <div className="flex items-center gap-3">
                              <FolderTree className="w-5 h-5 text-primary opacity-40" />
                              <div>
                                <h4 className="font-bold text-sm uppercase tracking-wider">{cat.name}</h4>
                                <p className="text-[10px] opacity-60">{cat.description || 'No description'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold opacity-40 uppercase tracking-widest">{catItems.length} Products</span>
                              <button 
                                onClick={() => {
                                  setEditingCategory(cat);
                                  setCategoryFormData({ name: cat.name, description: cat.description || '', status: cat.status });
                                  setIsCategoryModalOpen(true);
                                }}
                                className="p-1.5 hover:bg-black/5 rounded-lg transition-colors"
                              >
                                <Edit2 className="w-3 h-3 opacity-50" />
                              </button>
                              <button 
                                onClick={() => handleDeleteCategory(cat.id)}
                                className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div className="divide-y divide-[#141414]/5">
                            {catItems.length === 0 ? (
                              <div className="p-4 text-center text-[10px] opacity-30 italic">No products in this category</div>
                            ) : (
                              catItems.map(item => (
                                <div key={item.id} className="p-3 flex items-center justify-between hover:bg-[#141414]/5 transition-all group">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-[#141414]/5 flex items-center justify-center font-mono text-[10px] font-bold">
                                      {item.code.substring(0, 2)}
                                    </div>
                                    <div>
                                      <p className="text-xs font-bold">{item.name}</p>
                                      <p className="text-[10px] opacity-50 font-mono uppercase">{item.code}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-6">
                                    <div className="text-right">
                                      <p className="text-[10px] opacity-40 font-bold uppercase tracking-widest">Stock</p>
                                      <p className="text-xs font-bold font-mono">{item.quantity || 0}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-[10px] opacity-40 font-bold uppercase tracking-widest">Price</p>
                                      <p className="text-xs font-bold font-mono">${(item.unitPrice || 0).toLocaleString()}</p>
                                    </div>
                                    <button 
                                      onClick={() => setViewingItem(item)}
                                      className="p-1.5 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                    >
                                      <Search className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Uncategorized Section */}
                {items.filter(i => i.type === 'product' && !i.categoryId).length > 0 && (
                   <div className="mt-8 space-y-4">
                     <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 px-4">Uncategorized Products</h4>
                     <div className="bg-white border border-[#141414]/10 rounded-2xl divide-y divide-[#141414]/5 overflow-hidden">
                       {items.filter(i => i.type === 'product' && !i.categoryId).map(item => (
                         <div key={item.id} className="p-3 flex items-center justify-between hover:bg-[#141414]/5 transition-all group">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-[#141414]/5 flex items-center justify-center font-mono text-[10px] font-bold">
                                {item.code.substring(0, 2)}
                              </div>
                              <div>
                                <p className="text-xs font-bold">{item.name}</p>
                                <p className="text-[10px] opacity-50 font-mono uppercase">{item.code}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-6">
                              <div className="text-right">
                                <p className="text-[10px] opacity-40 font-bold uppercase tracking-widest">Stock</p>
                                <p className="text-xs font-bold font-mono">{item.quantity || 0}</p>
                              </div>
                              <button 
                                onClick={() => setViewingItem(item)}
                                className="p-1.5 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-lg transition-all opacity-0 group-hover:opacity-100"
                              >
                                <Search className="w-3 h-3" />
                              </button>
                            </div>
                         </div>
                       ))}
                     </div>
                   </div>
                )}
              </div>
            )}

            {inventorySubTab === 'warehouses' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold flex items-center gap-2">
                    <Store className="w-5 h-5 opacity-40" />
                    Warehouses & Store Locations
                  </h3>
                  <button 
                    onClick={() => {
                      setEditingWarehouse(null);
                      setWarehouseFormData({ name: '', address: '', status: 'active' });
                      setIsWarehouseModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-xl hover:opacity-90 transition-all text-xs font-bold"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add New</span>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {stores.filter((s: any) => s.isWarehouse).length === 0 ? (
                    <div className="col-span-full p-12 text-center border-2 border-dashed border-[#141414]/10 rounded-2xl opacity-40 italic serif">
                      No warehouses found. Click "Add New" to create one.
                    </div>
                  ) : (
                    stores.filter((s: any) => s.isWarehouse).map(store => (
                      <div key={store.id} className="p-4 bg-white border border-[#141414] rounded-xl flex items-center justify-between group hover:shadow-md transition-all">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#141414]/5 rounded-lg flex items-center justify-center">
                            <Store className="w-5 h-5 opacity-40" />
                          </div>
                          <div>
                            <p className="font-bold text-sm">{store.name}</p>
                            <p className="text-[10px] opacity-50 truncate max-w-[150px]">{store.address || 'No address'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => {
                              setEditingWarehouse(store);
                              setWarehouseFormData({
                                name: store.name,
                                address: store.address || '',
                                status: store.status || 'active'
                              });
                              setIsWarehouseModalOpen(true);
                            }}
                            className="p-1.5 hover:bg-black/5 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-3.5 h-3.5 opacity-50" />
                          </button>
                          <button 
                            onClick={async () => {
                              if (confirm("Are you sure you want to delete this warehouse?")) {
                                try {
                                  if (getLocalAuthToken()) {
                                    const res = await fetch(`/api/organizations/${orgId}/stores/${store.id}`, { method: 'DELETE', headers: getLocalApiHeaders() });
                                    if (!res.ok) throw new Error('Could not delete warehouse');
                                    setStores(prev => prev.filter(s => s.id !== store.id));
                                    notify.confirm("Warehouse deleted");
                                    return;
                                  }
                                  await deleteDoc(doc(db, 'warehouses', store.id));
                                  notify.confirm("Warehouse deleted");
                                } catch (err) {
                                  handleFirestoreError(err, OperationType.DELETE, `warehouses/${store.id}`);
                                }
                              }
                            }}
                            className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {inventorySubTab === 'lots' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold flex items-center gap-2">
                    <Hash className="w-5 h-5 opacity-40" />
                    Lot Number Registry
                  </h3>
                  <button 
                    onClick={() => {
                      setEditingLot(null);
                      setLotFormData({ lotNumber: '', expiryDate: '', notes: '', productId: '' });
                      setIsLotModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-xl hover:opacity-90 transition-all text-xs font-bold"
                  >
                    <Plus className="w-4 h-4" />
                    <span>New Lot</span>
                  </button>
                </div>
                
                <div className="bg-white border border-[#141414] rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-[#141414] bg-[#141414]/5">
                          <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-60">Lot Number</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-60">Product</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-60">Expiry Date</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-60">Notes</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-60"></th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {lotNumbers.length === 0 ? (
                          <tr><td colSpan={5} className="p-12 text-center opacity-40 italic">No lot numbers recorded.</td></tr>
                        ) : (
                          lotNumbers.map(lot => (
                            <tr key={lot.id} className="border-b border-[#141414]/10 hover:bg-[#141414]/5 transition-all group">
                              <td className="p-4 font-mono font-bold"><span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-[10px] tracking-tight">{lot.lotNumber}</span></td>
                              <td className="p-4 font-bold">{items.find(i => i.id === lot.productId)?.name || 'Generic / Multiple'}</td>
                              <td className="p-4">
                                <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold", lot.expiryDate && new Date(lot.expiryDate) < new Date() ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600")}>
                                  {lot.expiryDate || 'No Expiry'}
                                </span>
                              </td>
                              <td className="p-4 opacity-50 truncate max-w-[200px] text-xs">{lot.notes || '-'}</td>
                              <td className="p-4 text-right">
                                <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => {
                                    setEditingLot(lot);
                                    setLotFormData({ lotNumber: lot.lotNumber, expiryDate: lot.expiryDate || '', notes: lot.notes || '', productId: lot.productId || '' });
                                    setIsLotModalOpen(true);
                                  }} className="p-1.5 hover:bg-black/5 rounded-lg"><Edit2 className="w-3 h-3 opacity-50" /></button>
                                  <button onClick={() => handleDeleteLot(lot.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 className="w-3 h-3" /></button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {inventorySubTab === 'scrap' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold flex items-center gap-2 text-red-600">
                    <Trash2 className="w-5 h-5" />
                    Scrap & Salvage Management
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-white border-2 border-red-100 rounded-3xl p-6 shadow-sm">
                    <h4 className="text-sm font-bold uppercase tracking-wider mb-6 flex items-center gap-2">
                       <Plus className="w-4 h-4 opacity-40" />
                       Record New Scrap Action
                    </h4>
                    <form onSubmit={handleScrapSubmit} className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase opacity-40 tracking-widest">Source Product</label>
                        <SearchableSelect 
                          value={scrapData.productId}
                          onChange={e => setScrapData({...scrapData, productId: e.target.value})}
                          options={[
                            { value: '', label: 'Select Product' },
                            ...items.filter(i => i.type === 'product' && i.status === 'active').map(i => ({ value: i.id, label: `${i.name} (${i.code})` }))
                          ]}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold uppercase opacity-40 tracking-widest">From Warehouse</label>
                          <SearchableSelect 
                            value={scrapData.warehouseId}
                            onChange={e => setScrapData({...scrapData, warehouseId: e.target.value})}
                            options={[
                              { value: '', label: 'Select Warehouse' },
                              ...stores.filter(s => s.isWarehouse).map(s => ({ value: s.id, label: s.name }))
                            ]}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold uppercase opacity-40 tracking-widest">Lot Number (Optional)</label>
                          <select 
                            value={scrapData.lotNumber}
                            onChange={e => setScrapData({...scrapData, lotNumber: e.target.value})}
                            className="w-full bg-[#141414]/5 border-none rounded-xl px-4 py-2.5 text-xs font-bold focus:ring-2 focus:ring-[#141414]/20 outline-none transition-all"
                          >
                             <option value="">No Lot</option>
                             {lotNumbers.filter(l => l.productId === scrapData.productId).map(l => (
                               <option key={l.id} value={l.lotNumber}>{l.lotNumber}</option>
                             ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold uppercase opacity-40 tracking-widest">Quantity</label>
                          <input 
                            type="number" 
                            min="1"
                            value={scrapData.quantity}
                            onChange={e => setScrapData({...scrapData, quantity: parseInt(e.target.value) || 0})}
                            className="w-full bg-[#141414]/5 border-none rounded-xl px-4 py-2.5 text-xs font-bold focus:ring-2 focus:ring-[#141414]/20 outline-none transition-all"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-bold uppercase opacity-40 tracking-widest">Action</label>
                          <select 
                            value={scrapData.action}
                            onChange={e => setScrapData({...scrapData, action: e.target.value as 'write-off' | 'salvage'})}
                            className="w-full bg-[#141414]/5 border-none rounded-xl px-4 py-2.5 text-xs font-bold focus:ring-2 focus:ring-[#141414]/20 outline-none transition-all"
                          >
                            <option value="write-off">Write-off (Loss)</option>
                            <option value="salvage">Salvage (Transform)</option>
                          </select>
                        </div>
                      </div>

                      {scrapData.action === 'salvage' && (
                        <div className="space-y-1.5 animate-in slide-in-from-top-2">
                           <label className="text-[10px] font-bold uppercase opacity-40 tracking-widest text-[#14746F]">Target Product (Result of salvage)</label>
                           <SearchableSelect 
                              value={scrapData.targetProductId}
                              onChange={e => setScrapData({...scrapData, targetProductId: e.target.value})}
                              options={[
                                { value: '', label: 'Select Target Product' },
                                ...items.filter(i => i.type === 'product' && i.status === 'active' && i.id !== scrapData.productId).map(i => ({ value: i.id, label: `${i.name} (${i.code})` }))
                              ]}
                            />
                        </div>
                      )}

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase opacity-40 tracking-widest">Reason / Notes</label>
                        <textarea 
                          value={scrapData.reason}
                          onChange={e => setScrapData({...scrapData, reason: e.target.value})}
                          placeholder="Why are you scrapping this item?"
                          className="w-full bg-[#141414]/5 border-none rounded-xl px-4 py-2.5 text-xs font-bold focus:ring-2 focus:ring-[#141414]/20 outline-none transition-all min-h-[80px]"
                        />
                      </div>

                      <button 
                        type="submit"
                        disabled={isProcessing}
                        className="w-full bg-red-600 text-white py-3 rounded-2xl font-bold hover:bg-red-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-100"
                      >
                        {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        {scrapData.action === 'write-off' ? 'Process Write-off' : 'Process Salvage'}
                      </button>
                    </form>
                  </div>

                  <div className="space-y-6">
                    <div className="p-6 bg-yellow-50 rounded-3xl border border-yellow-100">
                      <h4 className="text-sm font-bold uppercase tracking-wider mb-2 flex items-center gap-2 text-yellow-800">
                        <AlertCircle className="w-4 h-4" />
                        Salvage Tip
                      </h4>
                      <p className="text-xs text-yellow-700 opacity-80 leading-relaxed italic serif">
                        Salvaging allows you to transform one product into another. For example, if a table is broken, you might salvage the wood as raw materials. This reduces stock of the table and increases stock of the wood.
                      </p>
                    </div>

                    <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100">
                      <h4 className="text-sm font-bold uppercase tracking-wider mb-2 flex items-center gap-2 text-blue-800">
                        <Package className="w-4 h-4" />
                        Stock Implications
                      </h4>
                      <ul className="text-[10px] text-blue-700 space-y-2 font-bold uppercase tracking-tight">
                        <li className="flex items-center gap-2">• Stock will be reduced instantly</li>
                        <li className="flex items-center gap-2">• A movement log will be created</li>
                        <li className="flex items-center gap-2">• Cost of goods will be adjusted</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {inventorySubTab === 'movement' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold uppercase tracking-widest flex items-center gap-2">
                    <RotateCcw className="w-5 h-5" />
                    Stock Movement History
                  </h2>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <input 
                        type="date"
                        value={reportFilters.startDate}
                        onChange={e => setReportFilters(prev => ({...prev, startDate: e.target.value}))}
                        className="bg-white border border-[#141414] rounded-lg px-2 py-1.5 text-xs font-bold outline-none"
                      />
                      <span className="text-xs font-bold opacity-50">to</span>
                      <input 
                        type="date"
                        value={reportFilters.endDate}
                        onChange={e => setReportFilters(prev => ({...prev, endDate: e.target.value}))}
                        className="bg-white border border-[#141414] rounded-lg px-2 py-1.5 text-xs font-bold outline-none"
                      />
                    </div>
                    <button 
                      onClick={exportMovementReport}
                      className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-full hover:opacity-90 transition-opacity text-xs font-bold"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Export CSV</span>
                    </button>
                  </div>
                </div>
                <div className="bg-white/50 border border-[#141414] rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse font-mono">
                      <thead>
                        <tr className="border-b border-[#141414] bg-[#141414] text-[#E4E3E0]">
                          <th className="p-4 text-xs font-bold uppercase tracking-wider">Ref #</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider">Product</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider text-center">Type</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider">From</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider">To</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider text-right">Qty</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider text-right">Value</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider">Date</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {movements.length === 0 ? (
                          <tr><td colSpan={8} className="p-12 text-center opacity-50 italic">No movements recorded yet.</td></tr>
                        ) : (
                          movements.filter(m => {
                            let matches = false;
                            if (m.createdAt && m.createdAt.toDate) {
                              const d = m.createdAt.toDate().toISOString().split('T')[0];
                              matches = d >= reportFilters.startDate && d <= reportFilters.endDate;
                            } else {
                              matches = true;
                            }
                            return matches && (!reportFilters.movementType || m.type === reportFilters.movementType);
                          }).map(m => (
                            <tr key={m.id} className="border-b border-[#141414]/10 hover:bg-[#141414]/5 transition-colors">
                              <td className="p-4 font-bold">
                                {m.referenceId ? (
                                  <button 
                                    onClick={() => {
                                      if (!onNavigate) return;
                                      const ref = m.referenceNumber || '';
                                      if (ref.startsWith('INV-')) onNavigate('invoices', m.referenceId);
                                      else if (ref.startsWith('BILL-')) onNavigate('bills', m.referenceId);
                                      else if (ref.startsWith('PO-')) onNavigate('purchase_orders', m.referenceId);
                                      else if (ref.startsWith('DLV-') || ref.startsWith('DO-')) onNavigate('deliveries', m.referenceId);
                                      else if (ref.startsWith('QUOTE-')) onNavigate('quotes', m.referenceId);
                                      else if (ref.startsWith('RCPT-')) onNavigate('pos_history', m.referenceId);
                                      else if (m.type === 'sale') onNavigate('invoices', m.referenceId); 
                                      else if (m.type === 'purchase') onNavigate('bills', m.referenceId); 
                                    }}
                                    className="text-blue-500 hover:text-blue-600 hover:underline transition-all text-left"
                                  >
                                    {m.referenceNumber}
                                  </button>
                                ) : (
                                  m.referenceNumber
                                )}
                              </td>
                              <td className="p-4"><span className="font-bold">{m.productName || items.find(i => i.id === m.itemId)?.name || 'Unknown'}</span></td>
                              <td className="p-4 text-center">
                                <span className={cn(
                                  "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-tighter",
                                  m.type === 'sale' ? "bg-red-100 text-red-600" :
                                  m.type === 'purchase' ? "bg-green-100 text-green-600" :
                                  m.type === 'transfer' ? "bg-blue-100 text-blue-600" :
                                  "bg-orange-100 text-orange-600"
                                )}>
                                  {m.type}
                                </span>
                              </td>
                              <td className="p-4 text-[10px]">{m.fromWarehouseId ? stores.find(s => s.id === m.fromWarehouseId)?.name || 'Source' : '-'}</td>
                              <td className="p-4 text-[10px]">{m.toWarehouseId ? stores.find(s => s.id === m.toWarehouseId)?.name || 'Target' : '-'}</td>
                              <td className="p-4 text-right font-bold">{m.quantity}</td>
                              <td className="p-4 text-right">{m.value?.toLocaleString() || '0'} {currency}</td>
                              <td className="p-4 text-[10px] opacity-60">{m.createdAt?.toDate ? m.createdAt.toDate().toLocaleString() : '-'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {inventorySubTab === 'reporting' && (
              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold uppercase tracking-widest flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Inventory Report
                  </h2>
                  <button 
                    onClick={exportInventoryReport}
                    className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-full hover:opacity-90 transition-opacity text-xs font-bold"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Export CSV</span>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white/50 border border-[#141414] p-4 rounded-2xl">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase opacity-50 px-1">Product</label>
                    <select 
                      value={reportFilters.productId}
                      onChange={(e) => setReportFilters({...reportFilters, productId: e.target.value})}
                      className="w-full bg-white border border-[#141414] rounded-lg px-2 py-1.5 text-xs font-bold outline-none text-black"
                    >
                      <option value="">All Products</option>
                      {items.filter(i => i.type === 'product').map(i => (
                        <option key={i.id} value={i.id}>{i.name} ({i.code})</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase opacity-50 px-1">Warehouse</label>
                    <select 
                      value={reportFilters.warehouseId}
                      onChange={(e) => setReportFilters({...reportFilters, warehouseId: e.target.value})}
                      className="w-full bg-white border border-[#141414] rounded-lg px-2 py-1.5 text-xs font-bold outline-none text-black"
                    >
                      <option value="">All Warehouses</option>
                      {stores.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase opacity-50 px-1">Lot Number</label>
                    <input 
                      type="text"
                      value={reportFilters.lotNumber}
                      onChange={(e) => setReportFilters({...reportFilters, lotNumber: e.target.value})}
                      placeholder="Search Lot..."
                      className="w-full bg-white border border-[#141414] rounded-lg px-2 py-1.5 text-xs font-bold outline-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase opacity-50 px-1">Expiry Before</label>
                    <input 
                      type="date"
                      value={reportFilters.expiryBefore}
                      onChange={(e) => setReportFilters({...reportFilters, expiryBefore: e.target.value})}
                      className="w-full bg-white border border-[#141414] rounded-lg px-2 py-1.5 text-xs font-bold outline-none"
                    />
                  </div>
                </div>

                <div className="bg-white/50 border border-[#141414] rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse font-mono">
                      <thead>
                        <tr className="border-b border-[#141414] bg-[#141414] text-[#E4E3E0]">
                          <th className="p-4 text-xs font-bold uppercase tracking-wider">Product</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider">Warehouse</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider">Lot #</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider">Expiry</th>
                          <th className="p-4 text-xs font-bold uppercase tracking-wider text-right">Quantity</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {(() => {
                          const records: any[] = [];
                          items.forEach(item => {
                            if (item.type !== 'product' || item.status === 'archived') return;
                            const itemRecords = item.stockRecords || [];
                            itemRecords.forEach(rec => {
                              const matchesProduct = !reportFilters.productId || item.id === reportFilters.productId;
                              const matchesWarehouse = !reportFilters.warehouseId || rec.warehouseId === reportFilters.warehouseId;
                              const matchesLot = !reportFilters.lotNumber || rec.lotNumber?.toLowerCase().includes(reportFilters.lotNumber.toLowerCase());
                              const matchesExpiryBefore = !reportFilters.expiryBefore || (rec.expiryDate && rec.expiryDate <= reportFilters.expiryBefore);
                              if (matchesProduct && matchesWarehouse && matchesLot && matchesExpiryBefore) {
                                records.push({
                                  id: `${item.id}-${rec.id}`,
                                  productName: item.name,
                                  sku: item.code,
                                  warehouseName: rec.warehouseName,
                                  lotNumber: rec.lotNumber || '-',
                                  expiryDate: rec.expiryDate || '-',
                                  quantity: rec.quantity
                                });
                              }
                            });
                          });
                          if (records.length === 0) return <tr><td colSpan={5} className="p-12 text-center opacity-50 italic serif">No stock records found.</td></tr>;
                          return records.map(rec => (
                            <tr key={rec.id} className="border-b border-[#141414]/10 hover:bg-[#141414]/5">
                              <td className="p-4"><span className="font-bold">{rec.productName}</span><span className="block text-[10px] opacity-50">SKU: {rec.sku}</span></td>
                              <td className="p-4">{rec.warehouseName}</td>
                              <td className="p-4"><span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-[10px] font-bold">{rec.lotNumber}</span></td>
                              <td className="p-4">
                                <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold", rec.expiryDate !== '-' && new Date(rec.expiryDate) < new Date() ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600")}>
                                  {rec.expiryDate}
                                </span>
                              </td>
                              <td className="p-4 text-right font-bold text-primary">{rec.quantity.toLocaleString()}</td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {inventorySubTab === 'movement-by-delivery' && (
        <div className="p-6 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold uppercase tracking-widest flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              Stock Movement By Delivery
            </h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input 
                  type="date"
                  value={reportFilters.startDate}
                  onChange={e => setReportFilters(prev => ({...prev, startDate: e.target.value}))}
                  className="bg-white border border-[#141414] rounded-lg px-2 py-1.5 text-xs font-bold outline-none"
                />
                <span className="text-xs font-bold opacity-50">to</span>
                <input 
                  type="date"
                  value={reportFilters.endDate}
                  onChange={e => setReportFilters(prev => ({...prev, endDate: e.target.value}))}
                  className="bg-white border border-[#141414] rounded-lg px-2 py-1.5 text-xs font-bold outline-none"
                />
              </div>
              <button 
                onClick={exportMovementReport}
                className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-full hover:opacity-90 transition-opacity text-xs font-bold"
              >
                <Upload className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
            </div>
          </div>
          <div className="bg-white/50 border border-[#141414] rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse font-mono">
                <thead>
                  <tr className="border-b border-[#141414] bg-[#141414] text-[#E4E3E0]">
                    <th className="p-4 text-xs font-bold uppercase tracking-wider">Delivery Ref #</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-center">Type</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-wider">From</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-wider">To</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-right">Total Items</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-right">Total Qty</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-wider text-right">Total Value</th>
                    <th className="p-4 text-xs font-bold uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {movements.length === 0 ? (
                    <tr><td colSpan={8} className="p-12 text-center opacity-50 italic">No movements recorded yet.</td></tr>
                  ) : (
                    Object.values(
                      movements.filter(m => {
                        let matches = false;
                        if (m.createdAt && m.createdAt.toDate) {
                          const d = m.createdAt.toDate().toISOString().split('T')[0];
                          matches = d >= reportFilters.startDate && d <= reportFilters.endDate;
                        } else {
                          matches = true;
                        }
                        return matches && (!reportFilters.movementType || m.type === reportFilters.movementType);
                      }).reduce((acc, m) => {
                        const key = m.referenceNumber || m.referenceId || `unref-${m.id}`;
                        if (!acc[key]) {
                          acc[key] = {
                            id: key,
                            referenceNumber: m.referenceNumber || '-',
                            referenceId: m.referenceId,
                            type: m.type,
                            fromWarehouseId: m.fromWarehouseId,
                            toWarehouseId: m.toWarehouseId,
                            createdAt: m.createdAt,
                            totalQuantity: 0,
                            totalValue: 0,
                            totalItems: 0,
                            items: []
                          };
                        }
                        acc[key].totalQuantity += m.quantity || 0;
                        acc[key].totalValue += m.value || 0;
                        acc[key].totalItems += 1;
                        acc[key].items.push(m);
                        return acc;
                      }, {} as Record<string, any>)
                    ).sort((a: any, b: any) => {
                      const dateA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
                      const dateB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
                      return dateB - dateA;
                    }).map((gm: any) => (
                        <React.Fragment key={gm.id}>
                          <tr className={cn(
                            "border-b border-[#141414]/10 hover:bg-[#141414]/5 transition-colors cursor-pointer",
                            expandedDelivery === gm.id ? "bg-[#141414]/5" : ""
                          )} onClick={() => setExpandedDelivery(expandedDelivery === gm.id ? null : gm.id)}>
                            <td className="p-4 font-bold flex items-center gap-2">
                              {expandedDelivery === gm.id ? <div className="text-[10px]">▼</div> : <div className="text-[10px]">▶</div>}
                              {gm.referenceId ? (
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!onNavigate) return;
                                    const ref = gm.referenceNumber || '';
                                    if (ref.startsWith('INV-')) onNavigate('invoices', gm.referenceId);
                                    else if (ref.startsWith('BILL-')) onNavigate('bills', gm.referenceId);
                                    else if (ref.startsWith('PO-')) onNavigate('purchase_orders', gm.referenceId);
                                    else if (ref.startsWith('DLV-') || ref.startsWith('DO-')) onNavigate('deliveries', gm.referenceId);
                                    else if (ref.startsWith('QUOTE-')) onNavigate('quotes', gm.referenceId);
                                    else if (ref.startsWith('RCPT-')) onNavigate('pos_history', gm.referenceId);
                                    else if (gm.type === 'sale') onNavigate('invoices', gm.referenceId);
                                    else if (gm.type === 'purchase') onNavigate('bills', gm.referenceId);
                                  }}
                                  className="text-blue-500 hover:text-blue-600 hover:underline transition-all text-left"
                                >
                                  {gm.referenceNumber}
                                </button>
                              ) : (
                                gm.referenceNumber
                              )}
                            </td>
                            <td className="p-4 text-center">
                              <span className={cn(
                                "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-tighter",
                                gm.type === 'sale' ? "bg-red-100 text-red-600" :
                                gm.type === 'purchase' ? "bg-green-100 text-green-600" :
                                gm.type === 'transfer' ? "bg-blue-100 text-blue-600" :
                                "bg-orange-100 text-orange-600"
                              )}>
                                {gm.type}
                              </span>
                            </td>
                            <td className="p-4 text-[10px]">{gm.fromWarehouseId ? stores.find(s => s.id === gm.fromWarehouseId)?.name || 'Source' : '-'}</td>
                            <td className="p-4 text-[10px]">{gm.toWarehouseId ? stores.find(s => s.id === gm.toWarehouseId)?.name || 'Target' : '-'}</td>
                            <td className="p-4 text-right font-bold">{gm.totalItems}</td>
                            <td className="p-4 text-right font-bold">{gm.totalQuantity}</td>
                            <td className="p-4 text-right">{gm.totalValue?.toLocaleString() || '0'} {currency}</td>
                            <td className="p-4 text-[10px] opacity-60">{gm.createdAt?.toDate ? gm.createdAt.toDate().toLocaleString() : '-'}</td>
                          </tr>
                          {expandedDelivery === gm.id && (
                            <tr className="bg-[#141414]/5 border-b border-[#141414]/10">
                              <td colSpan={8} className="p-4">
                                <div className="bg-white/50 rounded-xl p-4 border border-[#141414]/10">
                                  <table className="w-full text-xs font-mono">
                                    <thead>
                                      <tr className="border-b border-[#141414]/10 text-[#141414]/60">
                                        <th className="py-2 text-left">Product</th>
                                        <th className="py-2 text-right">Quantity</th>
                                        <th className="py-2 text-right">Value</th>
                                        <th className="py-2 text-left pl-4">Note</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {gm.items.map((item: any, idx: number) => (
                                        <tr key={idx} className="border-b border-[#141414]/5 last:border-0 hover:bg-white transition-colors">
                                          <td className="py-2 font-bold">{item.productName || items.find(i => i.id === item.itemId)?.name || 'Unknown'}</td>
                                          <td className="py-2 text-right">{item.quantity}</td>
                                          <td className="py-2 text-right">{item.value?.toLocaleString() || '0'}</td>
                                          <td className="py-2 pl-4 text-[#141414]/60 truncate max-w-[200px]">{item.note || '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                
                                <div className="mt-6 border-t border-[#141414]/10 pt-6">
                                  <h4 className="text-xs font-bold uppercase tracking-widest mb-4 opacity-50">History & Notes</h4>
                                  <HistoryNotesView 
                                    orgId={orgId}
                                    entityId={gm.id}
                                    entityType="Movement"
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {inventorySubTab === 'direct-delivery' && (
        <div className="max-w-4xl mx-auto p-6 space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
          <div className="bg-white/50 border border-[#141414] p-8 rounded-3xl shadow-2xl">
            <h2 className="text-2xl font-bold uppercase tracking-widest mb-6 flex items-center gap-3">
              <Plus className="w-6 h-6" />
              Direct Delivery / Adjustment
            </h2>
            <form onSubmit={handleDirectMovement} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase opacity-50 px-1">Movement Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['sale', 'purchase', 'transfer', 'return'].map((t) => (
                      <button key={t} type="button" onClick={() => setDirectMovement({...directMovement, type: t as any})} className={cn("py-2 rounded-xl text-[10px] font-bold uppercase tracking-wider border transition-all", directMovement.type === t ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" : "bg-white border-[#141414]/20 opacity-60 hover:opacity-100")}>{t}</button>
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  {directMovement.items.map((item, idx) => (
                    <div key={idx} className="p-4 border border-[#141414]/10 rounded-xl space-y-4 relative bg-[#141414]/5">
                      {directMovement.items.length > 1 && (
                        <button type="button" onClick={() => {
                          const newItems = [...directMovement.items];
                          newItems.splice(idx, 1);
                          setDirectMovement({ ...directMovement, items: newItems });
                        }} className="absolute -top-3 -right-3 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-lg">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      <div className="space-y-1">
                        <label className="text-xs font-bold uppercase opacity-50 px-1">Product</label>
                        <SearchableSelect 
                          value={item.productId} 
                          onChange={(e) => {
                            const newItems = [...directMovement.items];
                            newItems[idx].productId = e.target.value;
                            setDirectMovement({...directMovement, items: newItems});
                          }}
                          options={[
                            { value: '', label: 'Select Product...' },
                            ...items.filter(i => i.type === 'product' && i.status !== 'archived').map(i => ({
                              value: i.id,
                              label: `${i.name} (${i.code})`
                            }))
                          ]}
                        />
                      </div>
                         <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="text-xs font-bold uppercase opacity-50 px-1">Quantity</label>
                          <input required type="number" min="1" value={item.quantity} onChange={(e) => {
                            const newItems = [...directMovement.items];
                            newItems[idx].quantity = parseFloat(e.target.value) || 0;
                            setDirectMovement({...directMovement, items: newItems});
                          }} className="w-full bg-white border border-[#141414] p-3 rounded-xl outline-none font-bold font-mono" />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-bold uppercase opacity-50 px-1">Lot Number</label>
                          <SearchableSelect 
                            value={item.lotNumber} 
                            onChange={(e) => {
                              const lotNo = e.target.value;
                              const product = items.find(i => i.id === item.productId);
                              const internalLots = product?.stockRecords?.map(sr => ({
                                lotNumber: sr.lotNumber,
                                expiryDate: sr.expiryDate || ''
                              })) || [];
                              
                              const registryLot = lotNumbers.find(l => l.lotNumber === lotNo && l.productId === item.productId);
                              const internalLot = internalLots.find(l => l.lotNumber === lotNo);
                              
                              const expiry = registryLot?.expiryDate || internalLot?.expiryDate || '';
                              
                              const newItems = [...directMovement.items];
                              newItems[idx].lotNumber = lotNo;
                              if (expiry) {
                                newItems[idx].expiryDate = expiry;
                              }
                              setDirectMovement({...directMovement, items: newItems});
                            }} 
                            options={[
                              { value: '', label: 'Select Lot...' },
                              ...(() => {
                                const product = items.find(i => i.id === item.productId);
                                const internalLots = Array.from(new Set(product?.stockRecords?.map(sr => sr.lotNumber).filter(l => l && l !== 'AUTO') || []));
                                const registryLots = lotNumbers.filter(l => l.productId === item.productId).map(l => l.lotNumber);
                                const combinedLots = Array.from(new Set([...internalLots, ...registryLots]));
                                
                                return combinedLots.sort().map(lot => ({ value: lot, label: lot }));
                              })(),
                              { value: 'custom', label: '-- Manual Entry --' }
                            ]}
                          />
                          {item.lotNumber === 'custom' && (
                            <input 
                              type="text" 
                              placeholder="Enter Lot#"
                              onChange={(e) => {
                                const newItems = [...directMovement.items];
                                newItems[idx].lotNumber = e.target.value;
                                setDirectMovement({...directMovement, items: newItems});
                              }}
                              className="w-full mt-2 bg-white border border-[#141414] p-2 rounded-xl outline-none font-bold text-xs"
                            />
                          )}
                        </div>
                        <div className="space-y-1 col-span-2">
                          <label className="text-xs font-bold uppercase opacity-50 px-1">Expiry Date</label>
                          <input type="date" value={item.expiryDate} onChange={(e) => {
                            const newItems = [...directMovement.items];
                            newItems[idx].expiryDate = e.target.value;
                            setDirectMovement({...directMovement, items: newItems});
                          }} className="w-full bg-white border border-[#141414] p-3 rounded-xl outline-none font-bold" />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <button type="button" onClick={() => setDirectMovement({
                    ...directMovement,
                    items: [...directMovement.items, { productId: '', quantity: 1, lotNumber: '', expiryDate: '' }]
                  })} className="w-full py-3 rounded-xl border-2 border-dashed border-[#141414]/20 hover:border-[#141414]/40 hover:bg-[#141414]/5 transition-all flex items-center justify-center gap-2 text-sm font-bold uppercase opacity-70">
                    <Plus className="w-4 h-4" /> Add Product
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-1"><label className="text-xs font-bold uppercase opacity-50 px-1">Reference #</label><input type="text" placeholder="Optional" value={directMovement.reference} onChange={(e) => setDirectMovement({...directMovement, reference: e.target.value})} className="w-full bg-white border border-[#141414] p-3 rounded-xl outline-none font-bold" /></div>
                
                {(directMovement.type === 'sale' || directMovement.type === 'transfer' || directMovement.type === 'return') && (
                  <div className="space-y-1">
                    <label className="text-xs font-bold uppercase opacity-50 px-1">From Location (Source)</label>
                    <SearchableSelect 
                      value={directMovement.fromWarehouseId} 
                      onChange={(e) => setDirectMovement({...directMovement, fromWarehouseId: e.target.value})}
                      options={[
                        { value: '', label: 'Select Location...' },
                        ...stores.map((s: any) => ({
                          value: s.id,
                          label: `${s.name} ${s.isWarehouse ? '(Warehouse)' : '(Store)'}`
                        }))
                      ]}
                    />
                  </div>
                )}
                {(directMovement.type === 'purchase' || directMovement.type === 'transfer' || directMovement.type === 'return') && (
                  <div className="space-y-1">
                    <label className="text-xs font-bold uppercase opacity-50 px-1">To Location (Target)</label>
                    <SearchableSelect 
                      value={directMovement.toWarehouseId} 
                      onChange={(e) => setDirectMovement({...directMovement, toWarehouseId: e.target.value})}
                      options={[
                        { value: '', label: 'Select Location...' },
                        ...stores.map((s: any) => ({
                          value: s.id,
                          label: `${s.name} ${s.isWarehouse ? '(Warehouse)' : '(Store)'}`
                        }))
                      ]}
                    />
                  </div>
                )}
                <div className="space-y-1"><label className="text-xs font-bold uppercase opacity-50 px-1">Notes</label><textarea rows={2} value={directMovement.notes} onChange={(e) => setDirectMovement({...directMovement, notes: e.target.value})} className="w-full bg-white border border-[#141414] p-3 rounded-xl outline-none font-bold text-sm" /></div>
              </div>
              <div className="md:col-span-2 pt-4 border-t border-[#141414]/10"><button disabled={isProcessing} type="submit" className="w-full bg-[#141414] text-[#E4E3E0] py-4 rounded-2xl font-bold uppercase tracking-[.25em] transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3 shadow-xl">{isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5 text-green-400" />}<span>{isProcessing ? 'Processing...' : 'Confirm Stock Movement'}</span></button></div>
            </form>
          </div>
        </div>
      )}

        </div>
      </div>
    )}

      {!isInventoryMode && filter !== 'inventory' && (
        <div className="bg-white/50 border border-[#141414] rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#141414]">
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">SKU</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">Name</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">Type</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">Description</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif text-center">POS</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif text-center">Allow Negative</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">Cost Price</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">Sale Price</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif">{language === 'ar' ? 'الكمية' : 'Quantity'}</th>
                <th className="p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="p-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto opacity-20" />
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={11} className="p-12 text-center opacity-50 italic serif">
                    No items found matching your criteria.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr 
                    key={item.id} 
                    onClick={() => setViewingItem(item)}
                    className="border-b border-[#141414]/10 hover:bg-[#141414] hover:text-[#E4E3E0] transition-all group cursor-pointer"
                  >
                    <td className="p-4 font-mono text-sm font-bold">{item.code}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold">{item.name}</p>
                        {item.status === 'archived' && (
                          <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-[8px] font-bold uppercase tracking-wider group-hover:bg-white/20 group-hover:text-white">
                            Archived
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={cn(
                        "flex items-center w-fit gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider",
                        item.type === 'product' ? "bg-blue-100 text-blue-600" : "bg-purple-100 text-purple-600",
                        "group-hover:bg-white/20 group-hover:text-white"
                      )}>
                        {item.type === 'product' ? <Tag className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
                        {item.type}
                      </span>
                    </td>
                    <td className="p-4 text-sm opacity-70 truncate max-w-xs">{item.description}</td>
                    <td className="p-4">
                      {item.enablePOS && (
                        <div className="flex justify-center">
                          <div className="bg-green-100 text-green-600 p-1 rounded-full group-hover:bg-white/20 group-hover:text-white">
                            <Check className="w-3 h-3" />
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      {item.allowNegativeStock && (
                        <div className="flex justify-center">
                          <div className="bg-green-100 text-green-600 p-1 rounded-full group-hover:bg-white/20 group-hover:text-white">
                            <Check className="w-3 h-3" />
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="p-4 font-mono font-bold">
                      {item.costPrice !== undefined ? `$${item.costPrice.toLocaleString()}` : <span className="opacity-30">-</span>}
                    </td>
                    <td className="p-4 font-mono font-bold">
                      ${(item.unitPrice || 0).toLocaleString()}
                    </td>
                    <td className="p-4 font-mono font-bold">
                      {item.quantity !== undefined ? item.quantity.toLocaleString() : <span className="opacity-30">0</span>}
                    </td>
                    <td className="p-4 relative">
                      {item.status === 'archived' ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveItem(item.id, 'archived');
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#141414] text-[#E4E3E0] rounded-full text-[10px] font-bold uppercase tracking-wider hover:opacity-90 transition-all"
                        >
                          <RotateCcw className="w-3 h-3" />
                          Restore
                        </button>
                      ) : (
                        <>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (activeMenu !== item.id) announceDropdownOpen();
                              setActiveMenu(activeMenu === item.id ? null : item.id);
                            }}
                            className="p-2 hover:bg-white/20 rounded-full transition-colors"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          
                          {activeMenu === item.id && (
                            <div className="absolute right-4 top-12 w-48 bg-[#E4E3E0] border border-[#141414] rounded-xl shadow-xl z-50 overflow-hidden">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingItem(item);
                                  setActiveMenu(null);
                                }}
                                className="w-full text-left px-4 py-3 text-xs font-bold text-[#141414] hover:bg-[#141414] hover:text-[#E4E3E0] transition-all border-b border-[#141414]/10"
                              >{language === 'ar' ? 'تعديل الصنف' : 'Edit Item'}</button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchiveItem(item.id, item.status);
                                }}
                                className="w-full text-left px-4 py-3 text-xs font-bold text-[#141414] hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
                              >
                                Archive Item
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {/* New Item Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-2xl rounded-3xl p-8 shadow-2xl overflow-y-auto overscroll-contain max-h-[calc(100dvh-2rem)] custom-scrollbar">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold italic serif">{language === 'ar' ? 'إنشاء صنف جديد' : 'Create New Item'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddItem} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Type</label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setNewItem(prev => ({ ...prev, type: 'product' }))}
                      className={cn(
                        "flex-1 py-3 rounded-xl border border-[#141414] font-bold transition-all",
                        newItem.type === 'product' ? "bg-[#141414] text-[#E4E3E0]" : "hover:bg-[#141414]/5"
                      )}
                    >
                      Product
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewItem(prev => ({ ...prev, type: 'service' }))}
                      className={cn(
                        "flex-1 py-3 rounded-xl border border-[#141414] font-bold transition-all",
                        newItem.type === 'service' ? "bg-[#141414] text-[#E4E3E0]" : "hover:bg-[#141414]/5"
                      )}
                    >
                      Service
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">SKU (Stock Keeping Unit) *</label>
                  <input 
                    type="text" 
                    required
                    placeholder="e.g. SKU-PROD-001"
                    value={newItem.code || ''}
                    onChange={(e) => setNewItem(prev => ({ ...prev, code: e.target.value }))}
                    className={cn(
                      "w-full bg-white/50 border p-3 rounded-xl outline-none transition-all",
                      isDuplicateCode ? "border-red-500 bg-red-50" : "border-[#141414]"
                    )}
                  />
                  {isDuplicateCode && (
                    <p className="text-[10px] text-red-500 font-bold mt-1 uppercase tracking-wider">This SKU already exists</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Name *</label>
                  <input 
                    type="text" 
                    required
                    placeholder={language === 'ar' ? "اسم الصنف" : "Item Name"}
                    value={newItem.name || ''}
                    onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Category</label>
                  <SearchableSelect 
                    value={newItem.categoryId}
                    onChange={(e) => setNewItem(prev => ({ ...prev, categoryId: e.target.value }))}
                    options={[
                      { value: '', label: 'No Category' },
                      ...categories.map(cat => ({
                        value: cat.id,
                        label: cat.name
                      }))
                    ]}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Description</label>
                  <textarea 
                    rows={3}
                    placeholder="Detailed description..."
                    value={newItem.description || ''}
                    onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Cost Price</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={newItem.costPrice || ''}
                    onChange={(e) => setNewItem(prev => ({ ...prev, costPrice: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Sale Price *</label>
                  <input 
                    type="number" 
                    required
                    step="0.01"
                    value={newItem.unitPrice || ''}
                    onChange={(e) => setNewItem(prev => ({ ...prev, unitPrice: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">{language === 'ar' ? 'الكمية الإجمالية' : 'Total Quantity'}</label>
                  <input 
                    type="number" 
                    readOnly
                    value={(newItem.stockRecords || []).length > 0 
                      ? (newItem.stockRecords || []).reduce((sum, r) => sum + (r.quantity || 0), 0)
                      : (newItem.quantity || 0)
                    }
                    className="w-full bg-gray-100 border border-[#141414] p-3 rounded-xl outline-none cursor-not-allowed opacity-70"
                  />
                  <p className="text-[10px] opacity-40 mt-1 italic">Calculated from warehouse stocks</p>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Tax Rate</label>
                  <SearchableSelect 
                    value={newItem.taxRate}
                    onChange={(e) => setNewItem(prev => ({ ...prev, taxRate: e.target.value }))}
                    options={[
                      { value: '0%', label: 'Tax Exempt (0%)' },
                      ...taxRates.map(tax => ({
                        value: tax.name,
                        label: `${tax.name} (${tax.rate}%)`
                      }))
                    ]}
                  />
                </div>
                
                {newItem.type === 'product' && renderUomSelectors(newItem, next => setNewItem(next))}

                {newItem.type === 'product' && (
                  <div className="col-span-2 space-y-4">
                    <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                      <input 
                        type="checkbox" 
                        id="trackInventory"
                        checked={!!newItem.trackInventory}
                        onChange={(e) => setNewItem(prev => ({ ...prev, trackInventory: e.target.checked }))}
                        className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                      />
                      <label htmlFor="trackInventory" className="text-sm font-bold cursor-pointer select-none">{language === 'ar' ? 'تتبع المخزون (المنتجات فقط)' : 'Track Inventory (products only)'}<span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                          Inventory-tracked products create stock movements; services never affect stock.
                        </span>
                      </label>
                    </div>

                    {/* STOCK RECORDS SECTION */}
                    <div className="p-6 bg-white/40 border border-[#141414] rounded-2xl space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                          <Package className="w-4 h-4" />
                          {language === 'ar' ? 'المخزون حسب المستودع' : 'Stock by Warehouse'}
                        </h3>
                        <button
                          type="button"
                          onClick={() => setNewItem((prev: any) => ({ 
                            ...prev, 
                            stockRecords: [...(prev.stockRecords || []), { id: Math.random().toString(36).substr(2, 9), warehouseId: '', warehouseName: '', quantity: 0 }]
                          }))}
                          className="flex items-center gap-1 px-3 py-1 bg-[#141414] text-[#E4E3E0] rounded-full text-[10px] font-bold uppercase tracking-wider hover:opacity-90 transition-all"
                        >
                          <Plus className="w-3 h-3" />
                          {language === 'ar' ? 'إضافة مخزون' : 'Add Stock Line'}
                        </button>
                      </div>

                      <div className="space-y-3">
                        {((newItem.stockRecords) || []).map((record, index) => (
                          <div key={record.id} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end p-4 bg-white/50 rounded-xl border border-[#141414]/5">
                            <div className="md:col-span-1">
                              <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Warehouse</label>
                              <SearchableSelect
                                value={record.warehouseId}
                                onChange={(e) => {
                                  const selected = stores.find(s => s.id === e.target.value);
                                  setNewItem((prev: any) => {
                                    const newRecords = [...(prev.stockRecords || [])];
                                    newRecords[index] = { ...newRecords[index], warehouseId: e.target.value, warehouseName: selected?.name || '' };
                                    return { ...prev, stockRecords: newRecords };
                                  });
                                }}
                                options={[
                                  { value: '', label: 'Select Warehouse' },
                                  ...stores.map(s => ({ value: s.id, label: s.name }))
                                ]}
                                className="text-xs"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Lot #</label>
                              <input
                                type="text"
                                placeholder={language === 'ar' ? "رقم اللوط" : "Lot #"}
                                value={record.lotNumber || ''}
                                onChange={(e) => {
                                  setNewItem((prev: any) => {
                                    const newRecords = [...(prev.stockRecords || [])];
                                    newRecords[index].lotNumber = e.target.value;
                                    return { ...prev, stockRecords: newRecords };
                                  });
                                }}
                                className="w-full bg-white border border-[#141414] p-2 rounded-lg outline-none text-xs"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Expiry</label>
                              <input
                                type="date"
                                value={record.expiryDate || ''}
                                onChange={(e) => {
                                  setNewItem((prev: any) => {
                                    const newRecords = [...(prev.stockRecords || [])];
                                    newRecords[index].expiryDate = e.target.value;
                                    return { ...prev, stockRecords: newRecords };
                                  });
                                }}
                                className="w-full bg-white border border-[#141414] p-2 rounded-lg outline-none text-xs"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Qty</label>
                                <input
                                  type="number"
                                  value={record.quantity}
                                  onChange={(e) => {
                                    setNewItem((prev: any) => {
                                      const newRecords = [...(prev.stockRecords || [])];
                                      newRecords[index].quantity = parseFloat(e.target.value) || 0;
                                      return { ...prev, stockRecords: newRecords };
                                    });
                                  }}
                                  className="w-full bg-white border border-[#141414] p-2 rounded-lg outline-none text-xs text-center"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setNewItem((prev: any) => ({ 
                                    ...prev, 
                                    stockRecords: prev.stockRecords?.filter((_: any, i: number) => i !== index)
                                  }));
                                }}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {(newItem.stockRecords || []).length === 0 && (
                          <p className="text-[10px] opacity-40 italic text-center py-4">No warehouse stocks defined. Add lines to set initial quantities.</p>
                        )}
                      </div>
                    </div>

                    {/* LOTS SECTION */}
                    <div className="p-6 bg-white/40 border border-[#141414] rounded-2xl space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          Lot Numbers & Expiry Dates
                        </h3>
                        <button
                          type="button"
                          onClick={() => setNewItem(prev => ({ 
                            ...prev, 
                            lots: [...(prev.lots || []), { id: Math.random().toString(36).substr(2, 9), lotNumber: '', expiryDate: '' }]
                          }))}
                          className="flex items-center gap-1 px-3 py-1 bg-[#141414] text-[#E4E3E0] rounded-full text-[10px] font-bold uppercase tracking-wider hover:opacity-90 transition-all"
                        >
                          <Plus className="w-3 h-3" />
                          Add Lot
                        </button>
                      </div>

                      <div className="space-y-3">
                        {(newItem.lots || []).map((lot, index) => (
                          <div key={lot.id} className="grid grid-cols-2 gap-3 items-end p-4 bg-white/50 rounded-xl border border-[#141414]/5">
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Lot Number {index + 1}</label>
                              <input
                                type="text"
                                placeholder="Lot #"
                                value={lot.lotNumber}
                                onChange={(e) => {
                                  const newLots = [...(newItem.lots || [])];
                                  newLots[index].lotNumber = e.target.value;
                                  setNewItem(prev => ({ ...prev, lots: newLots }));
                                }}
                                className="w-full bg-white border border-[#141414] p-2 rounded-lg outline-none text-xs"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Expiry Date</label>
                                <div className="relative">
                                  <input
                                    type="date"
                                    value={lot.expiryDate}
                                    onChange={(e) => {
                                      const newLots = [...(newItem.lots || [])];
                                      newLots[index].expiryDate = e.target.value;
                                      setNewItem(prev => ({ ...prev, lots: newLots }));
                                    }}
                                    className="w-full bg-white border border-[#141414] p-2 pr-8 rounded-lg outline-none text-xs"
                                  />
                                  <Calendar className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none" />
                                </div>
                              </div>
                              {(newItem.lots || []).length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewItem(prev => ({ 
                                      ...prev, 
                                      lots: prev.lots?.filter((_, i) => i !== index)
                                    }));
                                  }}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        {(!newItem.lots || newItem.lots.length === 0) && (
                          <p className="text-[10px] opacity-40 italic text-center py-4">No lots added. Click "Add Lot" to track by lot number.</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-span-2 space-y-3">
                  <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                    <input 
                      type="checkbox" 
                      id="enablePOS"
                      checked={!!newItem.enablePOS}
                      onChange={(e) => setNewItem(prev => ({ ...prev, enablePOS: e.target.checked }))}
                      className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                    />
                    <label htmlFor="enablePOS" className="text-sm font-bold cursor-pointer select-none">
                      Enable POS
                      <span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                        Make this item available in the Point of Sale system
                      </span>
                    </label>
                  </div>

                  {newItem.enablePOS && (
                    <div className="p-6 bg-white/40 border border-[#141414] rounded-2xl space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">
                      <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        POS Options
                      </h3>
                      
                      {/* Image Upload */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-widest opacity-50">Product Image</label>
                        <div className="flex items-center gap-4">
                          <div className="w-24 h-24 border-2 border-dashed border-[#141414]/20 rounded-xl flex items-center justify-center overflow-hidden bg-white/50">
                            {newItem.imageUrl ? (
                              <img src={newItem.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                              <Package className="w-8 h-8 opacity-20" />
                            )}
                          </div>
                          <div className="flex-1 space-y-2">
                            <input 
                              type="file" 
                              accept="image/*"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  if (getLocalAuthToken()) {
                                    const imageUrl = await uploadLocalItemImage(file);
                                    setNewItem(prev => ({ ...prev, imageUrl }));
                                  } else {
                                    const storageRef = ref(storage, `items/${Date.now()}-${file.name}`);
                                    const snapshot = await uploadBytes(storageRef, file);
                                    const url = await getDownloadURL(snapshot.ref);
                                    setNewItem(prev => ({ ...prev, imageUrl: url }));
                                  }
                                  notify.confirm('Image uploaded successfully');
                                } catch (err) {
                                  console.error(err);
                                  notify.error('Failed to upload image');
                                }
                              }}
                              className="hidden" 
                              id="newItemImage" 
                            />
                            <label 
                              htmlFor="newItemImage"
                              className="inline-block px-4 py-2 bg-[#141414] text-[#E4E3E0] text-xs font-bold rounded-full cursor-pointer hover:opacity-90 transition-all"
                            >
                              Upload Image
                            </label>
                            <p className="text-[10px] opacity-50">Recommended size: 800x800px. Max 2MB.</p>
                          </div>
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-widest opacity-50">Tags</label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {newItem.tags?.map((tag, idx) => (
                            <span key={idx} className="px-2 py-1 bg-[#141414] text-[#E4E3E0] text-[10px] font-bold rounded-full flex items-center gap-1">
                              {tag}
                              <button 
                                type="button"
                                onClick={() => setNewItem(prev => ({ ...prev, tags: prev.tags?.filter((_, i) => i !== idx) }))}
                                className="hover:text-red-400"
                              >
                                <XCircle className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <input 
                          type="text"
                          placeholder="Add tag and press Enter..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = e.currentTarget.value.trim();
                              if (val && !newItem.tags?.includes(val)) {
                                setNewItem(prev => ({ ...prev, tags: [...(prev.tags || []), val] }));
                                e.currentTarget.value = '';
                              }
                            }
                          }}
                          className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none text-sm"
                        />
                      </div>

                      {/* Store Availability */}
                      <div className="space-y-4 pt-4 border-t border-[#141414]/10">
                        <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          Store Availability & Quantity
                        </h3>
                        <div className="space-y-3">
                          {stores.map(store => {
                            const availability = newItem.storeAvailability?.find(sa => sa.storeId === store.id);
                            return (
                              <div key={store.id} className="flex items-center gap-4 bg-white/30 p-3 rounded-xl border border-[#141414]/5">
                                <div className="flex-1">
                                  <label className="flex items-center gap-3 cursor-pointer select-none">
                                    <input 
                                      type="checkbox"
                                      checked={availability?.enabled ?? false}
                                      onChange={(e) => {
                                        const sa = newItem.storeAvailability || [];
                                        const exists = sa.find(item => item.storeId === store.id);
                                        const updated = exists 
                                          ? sa.map(item => item.storeId === store.id ? { ...item, enabled: e.target.checked } : item)
                                          : [...sa, { storeId: store.id, storeName: store.name, quantity: 0, enabled: e.target.checked }];
                                        setNewItem(prev => ({ ...prev, storeAvailability: updated }));
                                      }}
                                      className="w-4 h-4 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                                    />
                                    <span className="text-sm font-bold">{store.name} {(store as any).isWarehouse ? '(Warehouse)' : '(Store)'}</span>
                                  </label>
                                </div>
                                {availability?.enabled && (
                                  <div className="flex items-center gap-2">
                                    <label className="text-[10px] font-bold uppercase opacity-50">Stock Quantity</label>
                                    <input 
                                      type="number"
                                      value={availability.quantity}
                                      onChange={(e) => {
                                        const sa = newItem.storeAvailability || [];
                                        const updated = sa.map(item => item.storeId === store.id ? { ...item, quantity: parseInt(e.target.value) || 0 } : item);
                                        setNewItem(prev => ({ ...prev, storeAvailability: updated }));
                                      }}
                                      className="w-20 bg-white/50 border border-[#141414] p-2 rounded-lg text-xs outline-none text-center"
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Recipe Management */}
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            Recipe Management
                          </h3>
                          <button 
                            type="button"
                            onClick={() => setNewItem(prev => ({ 
                              ...prev, 
                              recipes: [...(prev.recipes || []), { id: Math.random().toString(36).substr(2, 9), name: '', isActive: prev.recipes?.length === 0, components: [] }] 
                            }))}
                            className="text-[10px] font-bold underline hover:opacity-70"
                          >
                            + Add New Recipe
                          </button>
                        </div>

                        <div className="space-y-4">
                          {newItem.recipes?.map((recipe, rIdx) => (
                            <div key={recipe.id} className="p-4 bg-white/30 border border-[#141414]/10 rounded-xl space-y-4">
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex-1 flex items-center gap-4">
                                  <input 
                                    type="text"
                                    placeholder="Recipe Name (e.g. Standard, Premium)"
                                    value={recipe.name}
                                    onChange={(e) => setNewItem(prev => ({
                                      ...prev,
                                      recipes: prev.recipes?.map((r, i) => i === rIdx ? { ...r, name: e.target.value } : r)
                                    }))}
                                    className="flex-1 bg-white/50 border border-[#141414] p-2 rounded-lg text-xs outline-none font-bold"
                                  />
                                  <div className="flex items-center gap-2">
                                    <input 
                                      type="checkbox"
                                      id={`recipe-active-${rIdx}`}
                                      checked={recipe.isActive}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setNewItem(prev => ({
                                            ...prev,
                                            recipes: prev.recipes?.map((r, i) => ({ ...r, isActive: i === rIdx }))
                                          }));
                                        }
                                      }}
                                      className="w-4 h-4 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                                    />
                                    <label htmlFor={`recipe-active-${rIdx}`} className="text-[10px] font-bold uppercase tracking-widest opacity-50 cursor-pointer">Active</label>
                                  </div>
                                </div>
                                <button 
                                  type="button"
                                  onClick={() => setNewItem(prev => ({ ...prev, recipes: prev.recipes?.filter((_, i) => i !== rIdx) }))}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-50">Components</label>
                                  <button 
                                    type="button"
                                    onClick={() => setNewItem(prev => ({
                                      ...prev,
                                      recipes: prev.recipes?.map((r, i) => i === rIdx ? { ...r, components: [...r.components, { itemId: '', name: '', quantity: 1, unit: 'pcs' }] } : r)
                                    }))}
                                    className="text-[9px] font-bold underline hover:opacity-70"
                                  >
                                    + Add Component
                                  </button>
                                </div>
                                <div className="space-y-2">
                                  {recipe.components.map((ingredient, cIdx) => (
                                    <div key={cIdx} className="flex gap-2 items-start">
                                      <select 
                                        value={ingredient.itemId}
                                        onChange={(e) => {
                                          const selected = items.find(i => i.id === e.target.value);
                                          setNewItem(prev => ({
                                            ...prev,
                                            recipes: prev.recipes?.map((r, i) => i === rIdx ? {
                                              ...r,
                                              components: r.components.map((c, j) => j === cIdx ? { ...c, itemId: e.target.value, name: selected?.name || '' } : c)
                                            } : r)
                                          }));
                                        }}
                                        className="flex-1 bg-white/50 border border-[#141414] p-2 rounded-lg text-[10px] outline-none"
                                      >
                                        <option value="">Select Item</option>
                                        {items.filter(i => i.id !== newItem.id).map(i => (
                                          <option key={i.id} value={i.id}>{i.name} ({i.code})</option>
                                        ))}
                                      </select>
                                      <input 
                                        type="number"
                                        placeholder="Qty"
                                        value={isNaN(ingredient.quantity) ? '' : ingredient.quantity}
                                        onChange={(e) => setNewItem(prev => ({
                                          ...prev,
                                          recipes: prev.recipes?.map((r, i) => i === rIdx ? {
                                            ...r,
                                            components: r.components.map((c, j) => j === cIdx ? { ...c, quantity: parseFloat(e.target.value) || 0 } : c)
                                          } : r)
                                        }))}
                                        className="w-16 bg-white/50 border border-[#141414] p-2 rounded-lg text-[10px] outline-none text-center"
                                      />
                                      <select
                                        value={ingredient.unit || ''}
                                        onChange={(e) => setNewItem(prev => ({
                                          ...prev,
                                          recipes: prev.recipes?.map((r, i) => i === rIdx ? {
                                            ...r,
                                            components: r.components.map((c, j) => j === cIdx ? { ...c, unit: e.target.value } : c)
                                          } : r)
                                        }))}
                                        className="w-24 bg-white/50 border border-[#141414] p-2 rounded-lg text-[10px] outline-none"
                                      >
                                        <option value="">UOM</option>
                                        {recipeUomOptions(ingredient.itemId, ingredient.unit).legacy && <option value={ingredient.unit}>{ingredient.unit} (legacy)</option>}
                                        {recipeUomOptions(ingredient.itemId, ingredient.unit).options.map(unit => <option key={unit.id} value={unit.name}>{unit.name}</option>)}
                                      </select>
                                      <button 
                                        type="button"
                                        onClick={() => setNewItem(prev => ({
                                          ...prev,
                                          recipes: prev.recipes?.map((r, i) => i === rIdx ? {
                                            ...r,
                                            components: r.components.filter((_, j) => j !== cIdx)
                                          } : r)
                                        }))}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                      >
                                        <XCircle className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Variant Management */}
                      <div className="space-y-4 pt-4 border-t border-[#141414]/10">
                        <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                          <input 
                            type="checkbox" 
                            id="hasVariants"
                            checked={!!newItem.hasVariants}
                            onChange={(e) => setNewItem(prev => ({ ...prev, hasVariants: e.target.checked }))}
                            className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                          />
                          <label htmlFor="hasVariants" className="text-sm font-bold cursor-pointer select-none">
                            Enable Variants
                            <span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                              Add options like size or color to create multiple versions of this product
                            </span>
                          </label>
                        </div>

                        {newItem.hasVariants && (
                          <div className="space-y-6 p-4 bg-white/30 border border-[#141414]/10 rounded-xl animate-in fade-in slide-in-from-top-2">
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <label className="text-xs font-bold uppercase tracking-widest opacity-50">Options</label>
                                <button 
                                  type="button"
                                  onClick={() => setNewItem(prev => ({
                                    ...prev,
                                    variantOptions: [...(prev.variantOptions || []), { name: '', values: [] }]
                                  }))}
                                  className="text-[10px] font-bold underline hover:opacity-70"
                                >
                                  + Add Option
                                </button>
                              </div>

                              <div className="space-y-4">
                                {newItem.variantOptions?.map((option, oIdx) => (
                                  <div key={oIdx} className="space-y-2 p-3 bg-white/50 rounded-lg border border-[#141414]/5">
                                    <div className="flex gap-2">
                                      <input 
                                        type="text"
                                        placeholder="Option Name (e.g. Color, Size)"
                                        value={option.name}
                                        onChange={(e) => setNewItem(prev => ({
                                          ...prev,
                                          variantOptions: prev.variantOptions?.map((o, i) => i === oIdx ? { ...o, name: e.target.value } : o)
                                        }))}
                                        className="flex-1 bg-white/50 border border-[#141414] p-2 rounded-lg text-xs outline-none font-bold"
                                      />
                                      <button 
                                        type="button"
                                        onClick={() => setNewItem(prev => ({
                                          ...prev,
                                          variantOptions: prev.variantOptions?.filter((_, i) => i !== oIdx)
                                        }))}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </button>
                                    </div>
                                    <div className="space-y-2">
                                      <div className="flex flex-wrap gap-2">
                                        {option.values.map((val, vIdx) => (
                                          <span key={vIdx} className="px-2 py-1 bg-[#141414] text-[#E4E3E0] text-[9px] font-bold rounded-full flex items-center gap-1">
                                            {val}
                                            <button 
                                              type="button"
                                              onClick={() => setNewItem(prev => ({
                                                ...prev,
                                                variantOptions: prev.variantOptions?.map((o, i) => i === oIdx ? { ...o, values: o.values.filter((_, j) => j !== vIdx) } : o)
                                              }))}
                                              className="hover:text-red-400"
                                            >
                                              <XCircle className="w-3 h-3" />
                                            </button>
                                          </span>
                                        ))}
                                      </div>
                                      <input 
                                        type="text"
                                        placeholder="Add value and press Enter..."
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const val = e.currentTarget.value.trim();
                                            if (val && !option.values.includes(val)) {
                                              setNewItem(prev => ({
                                                ...prev,
                                                variantOptions: prev.variantOptions?.map((o, i) => i === oIdx ? { ...o, values: [...o.values, val] } : o)
                                              }));
                                              e.currentTarget.value = '';
                                            }
                                          }
                                        }}
                                        className="w-full bg-white/50 border border-[#141414] p-2 rounded-lg text-[10px] outline-none"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <button 
                                type="button"
                                onClick={() => {
                                  const generated = generateVariants(
                                    newItem.variantOptions || [], 
                                    newItem.unitPrice || 0, 
                                    newItem.costPrice || 0,
                                    newItem.code || 'VAR'
                                  );
                                  setNewItem(prev => ({ ...prev, variants: generated }));
                                }}
                                className="w-full py-2 bg-[#141414] text-[#E4E3E0] text-[10px] font-bold rounded-lg hover:opacity-90 transition-all uppercase tracking-widest"
                              >
                                Generate Variants
                              </button>
                            </div>

                            {newItem.variants && newItem.variants.length > 0 && (
                              <div className="space-y-3">
                                <label className="text-xs font-bold uppercase tracking-widest opacity-50">Generated Variants</label>
                                <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar pr-2">
                                  <div className="grid grid-cols-7 gap-2 px-2 py-1 text-[9px] font-bold uppercase tracking-wider opacity-50">
                                    <div className="flex items-center justify-center">Save</div>
                                    <div className="col-span-2">Variant</div>
                                    <div>Cost Price</div>
                                    <div>Sell Price</div>
                                    <div>Barcode</div>
                                    <div className="text-center">Qty</div>
                                  </div>
                                  {newItem.variants.map((variant, vIdx) => (
                                    <div key={variant.id} className={cn(
                                      "grid grid-cols-7 gap-2 p-2 rounded-lg border items-center transition-all",
                                      variant.save ? "bg-white/50 border-[#141414]/5" : "bg-white/10 border-transparent opacity-50"
                                    )}>
                                      <div className="flex items-center justify-center">
                                        <input 
                                          type="checkbox"
                                          checked={!!variant.save}
                                          onChange={(e) => setNewItem(prev => ({
                                            ...prev,
                                            variants: prev.variants?.map((v, i) => i === vIdx ? { ...v, save: e.target.checked } : v)
                                          }))}
                                          className="w-4 h-4 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                                        />
                                      </div>
                                      <div className="col-span-2 text-[10px] font-bold truncate">
                                        {variant.name}
                                        <span className="block text-[8px] opacity-40 font-mono">{variant.sku}</span>
                                      </div>
                                      <input 
                                        type="number"
                                        placeholder="Cost"
                                        value={isNaN(variant.costPrice) ? '' : variant.costPrice}
                                        onChange={(e) => setNewItem(prev => ({
                                          ...prev,
                                          variants: prev.variants?.map((v, i) => i === vIdx ? { ...v, costPrice: parseFloat(e.target.value) || 0 } : v)
                                        }))}
                                        className="bg-white/50 border border-[#141414] p-1 rounded text-[10px] outline-none text-center"
                                      />
                                      <input 
                                        type="number"
                                        placeholder="Sell"
                                        value={isNaN(variant.unitPrice) ? '' : variant.unitPrice}
                                        onChange={(e) => setNewItem(prev => ({
                                          ...prev,
                                          variants: prev.variants?.map((v, i) => i === vIdx ? { ...v, unitPrice: parseFloat(e.target.value) || 0 } : v)
                                        }))}
                                        className="bg-white/50 border border-[#141414] p-1 rounded text-[10px] outline-none text-center"
                                      />
                                      <input 
                                        type="text"
                                        placeholder="Barcode"
                                        value={variant.barcode}
                                        onChange={(e) => setNewItem(prev => ({
                                          ...prev,
                                          variants: prev.variants?.map((v, i) => i === vIdx ? { ...v, barcode: e.target.value } : v)
                                        }))}
                                        className="bg-white/50 border border-[#141414] p-1 rounded text-[10px] outline-none"
                                      />
                                      <input 
                                        type="number"
                                        placeholder="Qty"
                                        value={isNaN(variant.quantity) ? '' : variant.quantity}
                                        onChange={(e) => setNewItem(prev => ({
                                          ...prev,
                                          variants: prev.variants?.map((v, i) => i === vIdx ? { ...v, quantity: parseInt(e.target.value) || 0 } : v)
                                        }))}
                                        className="bg-white/50 border border-[#141414] p-1 rounded text-[10px] outline-none text-center"
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Additional POS Options */}
                      <div className="space-y-3 pt-4 border-t border-[#141414]/10">
                        <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                          <input 
                            type="checkbox" 
                            id="allowNegativeStock"
                            checked={!!newItem.allowNegativeStock}
                            onChange={(e) => setNewItem(prev => ({ ...prev, allowNegativeStock: e.target.checked }))}
                            className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                          />
                          <label htmlFor="allowNegativeStock" className="text-sm font-bold cursor-pointer select-none">
                            Allow Negative Stock
                            <span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                              This will allow the product to be sold in negative even with no stock
                            </span>
                          </label>
                        </div>
                        <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                          <input 
                            type="checkbox" 
                            id="hasExpiry"
                            checked={!!newItem.hasExpiry}
                            onChange={(e) => setNewItem(prev => ({ ...prev, hasExpiry: e.target.checked }))}
                            className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                          />
                          <label htmlFor="hasExpiry" className="text-sm font-bold cursor-pointer select-none">
                            Product Has Expiry
                            <span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                              This will toggle the product that it will have an expiry date that need to be tracked
                            </span>
                          </label>
                        </div>
                        <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                          <input 
                            type="checkbox" 
                            id="hideFromSellScreen"
                            checked={!!newItem.hideFromSellScreen}
                            onChange={(e) => setNewItem(prev => ({ ...prev, hideFromSellScreen: e.target.checked }))}
                            className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                          />
                          <label htmlFor="hideFromSellScreen" className="text-sm font-bold cursor-pointer select-none">
                            Hide From Sell Screen
                            <span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                              The product will be hidden to be sold in sell screen but will still be visible here in products and composite component
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4 pt-4 border-t border-[#141414]">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-3 rounded-full border border-[#141414] font-bold hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={isDuplicateCode}
                  className={cn(
                    "flex-1 py-3 rounded-full font-bold transition-all",
                    isDuplicateCode 
                      ? "bg-[#141414]/20 text-[#141414]/40 cursor-not-allowed" 
                      : "bg-[#141414] text-[#E4E3E0] hover:opacity-90"
                  )}
                >{language === 'ar' ? 'حفظ الصنف' : 'Save Item'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Edit Item Modal */}
      {editingItem && (
        <div
          className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setEditingItem(null);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-2xl rounded-3xl p-8 shadow-2xl overflow-y-auto overscroll-contain max-h-[calc(100dvh-2rem)] custom-scrollbar">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold italic serif">{language === 'ar' ? 'تعديل الصنف' : 'Edit Item'}</h2>
              <button onClick={() => setEditingItem(null)} className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateItem} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Type</label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setEditingItem(prev => prev ? ({ ...prev, type: 'product' }) : null)}
                      className={cn(
                        "flex-1 py-3 rounded-xl border border-[#141414] font-bold transition-all",
                        editingItem.type === 'product' ? "bg-[#141414] text-[#E4E3E0]" : "hover:bg-[#141414]/5"
                      )}
                    >
                      Product
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingItem(prev => prev ? ({ ...prev, type: 'service' }) : null)}
                      className={cn(
                        "flex-1 py-3 rounded-xl border border-[#141414] font-bold transition-all",
                        editingItem.type === 'service' ? "bg-[#141414] text-[#E4E3E0]" : "hover:bg-[#141414]/5"
                      )}
                    >
                      Service
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">SKU (Stock Keeping Unit) *</label>
                  <input 
                    type="text" 
                    required
                    placeholder="e.g. SKU-001"
                    value={editingItem.code || ''}
                    onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, code: e.target.value }) : null)}
                    className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Name *</label>
                  <input 
                    type="text" 
                    required
                    placeholder={language === 'ar' ? "اسم الصنف" : "Item Name"}
                    value={editingItem.name || ''}
                    onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, name: e.target.value }) : null)}
                    className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Category</label>
                  <SearchableSelect 
                    value={editingItem.categoryId}
                    onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, categoryId: e.target.value }) : null)}
                    options={[
                      { value: '', label: 'No Category' },
                      ...categories.map(cat => ({
                        value: cat.id,
                        label: cat.name
                      }))
                    ]}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Description</label>
                  <textarea 
                    rows={3}
                    placeholder="Detailed description..."
                    value={editingItem.description || ''}
                    onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, description: e.target.value }) : null)}
                    className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Cost Price</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={editingItem.costPrice || 0}
                    onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, costPrice: parseFloat(e.target.value) || 0 }) : null)}
                    className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Sale Price *</label>
                  <input 
                    type="number" 
                    required
                    step="0.01"
                    value={editingItem.unitPrice}
                    onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, unitPrice: parseFloat(e.target.value) || 0 }) : null)}
                    className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">{language === 'ar' ? 'الكمية الإجمالية' : 'Total Quantity'}</label>
                  <input 
                    type="number" 
                    readOnly
                    value={(editingItem.stockRecords || []).length > 0 
                      ? (editingItem.stockRecords || []).reduce((sum, r) => sum + (r.quantity || 0), 0)
                      : (editingItem.quantity || 0)
                    }
                    className="w-full bg-gray-100 border border-[#141414] p-3 rounded-xl outline-none cursor-not-allowed opacity-70"
                  />
                  <p className="text-[10px] opacity-40 mt-1 italic">Calculated from warehouse stocks</p>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Tax Rate</label>
                  <select 
                    value={editingItem.taxRate || '0%'}
                    onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, taxRate: e.target.value }) : null)}
                    className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none"
                  >
                    <option value="0%">Tax Exempt (0%)</option>
                    {taxRates.map(tax => (
                      <option key={tax.id} value={tax.name}>{tax.name} ({tax.rate}%)</option>
                    ))}
                  </select>
                </div>

                {editingItem.type === 'product' && renderUomSelectors(
                  editingItem,
                  next => setEditingItem(next as Item),
                  movementItemIds.has(editingItem.id),
                )}

                {editingItem.type === 'product' && (
                  <div className="col-span-2 space-y-4">
                    <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                      <input 
                        type="checkbox" 
                        id="editTrackInventory"
                        checked={!!editingItem.trackInventory}
                        onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, trackInventory: e.target.checked }) : null)}
                        className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                      />
                      <label htmlFor="editTrackInventory" className="text-sm font-bold cursor-pointer select-none">{language === 'ar' ? 'تتبع المخزون (المنتجات فقط)' : 'Track Inventory (products only)'}<span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                          Inventory-tracked products create stock movements; services never affect stock.
                        </span>
                      </label>
                    </div>

                    {/* STOCK RECORDS SECTION */}
                    <div className="p-6 bg-white/40 border border-[#141414] rounded-2xl space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                          <Package className="w-4 h-4" />
                          {language === 'ar' ? 'المخزون حسب المستودع' : 'Stock by Warehouse'}
                        </h3>
                        <button
                          type="button"
                          onClick={() => setEditingItem((prev: any) => ({ 
                            ...prev, 
                            stockRecords: [...(prev.stockRecords || []), { id: Math.random().toString(36).substr(2, 9), warehouseId: '', warehouseName: '', quantity: 0 }]
                          }))}
                          className="flex items-center gap-1 px-3 py-1 bg-[#141414] text-[#E4E3E0] rounded-full text-[10px] font-bold uppercase tracking-wider hover:opacity-90 transition-all"
                        >
                          <Plus className="w-3 h-3" />
                          {language === 'ar' ? 'إضافة مخزون' : 'Add Stock Line'}
                        </button>
                      </div>

                      <div className="space-y-3">
                        {editingItem && (editingItem.stockRecords || []).map((record: any, index: number) => (
                          <div key={record.id} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end p-4 bg-white/50 rounded-xl border border-[#141414]/5">
                            <div className="md:col-span-1">
                              <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Warehouse</label>
                              <select
                                value={record.warehouseId}
                                onChange={(e) => {
                                  const selected = stores.find(s => s.id === e.target.value);
                                  setEditingItem((prev: any) => {
                                    if (!prev) return null;
                                    const newRecords = [...(prev.stockRecords || [])];
                                    newRecords[index] = { ...newRecords[index], warehouseId: e.target.value, warehouseName: selected?.name || '' };
                                    return { ...prev, stockRecords: newRecords };
                                  });
                                }}
                                className="w-full bg-white border border-[#141414] p-2 rounded-lg outline-none text-xs text-black"
                              >
                                <option value="">Select Warehouse</option>
                                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Lot #</label>
                              <input
                                type="text"
                                placeholder={language === 'ar' ? "رقم اللوط" : "Lot #"}
                                value={record.lotNumber || ''}
                                onChange={(e) => {
                                  setEditingItem((prev: any) => {
                                    if (!prev) return null;
                                    const newRecords = [...(prev.stockRecords || [])];
                                    newRecords[index].lotNumber = e.target.value;
                                    return { ...prev, stockRecords: newRecords };
                                  });
                                }}
                                className="w-full bg-white border border-[#141414] p-2 rounded-lg outline-none text-xs"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Expiry</label>
                              <input
                                type="date"
                                value={record.expiryDate || ''}
                                onChange={(e) => {
                                  setEditingItem((prev: any) => {
                                    if (!prev) return null;
                                    const newRecords = [...(prev.stockRecords || [])];
                                    newRecords[index].expiryDate = e.target.value;
                                    return { ...prev, stockRecords: newRecords };
                                  });
                                }}
                                className="w-full bg-white border border-[#141414] p-2 rounded-lg outline-none text-xs"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Qty</label>
                                <input
                                  type="number"
                                  value={record.quantity}
                                  onChange={(e) => {
                                    setEditingItem((prev: any) => {
                                      if (!prev) return null;
                                      const newRecords = [...(prev.stockRecords || [])];
                                      newRecords[index].quantity = parseFloat(e.target.value) || 0;
                                      return { ...prev, stockRecords: newRecords };
                                    });
                                  }}
                                  className="w-full bg-white border border-[#141414] p-2 rounded-lg outline-none text-xs text-center"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingItem((prev: any) => {
                                    if (!prev) return null;
                                    return { 
                                      ...prev, 
                                      stockRecords: prev.stockRecords?.filter((_: any, i: number) => i !== index)
                                    };
                                  });
                                }}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {editingItem && (editingItem.stockRecords || []).length === 0 && (
                          <p className="text-[10px] opacity-40 italic text-center py-4">No warehouse stocks defined. Add lines to set initial quantities.</p>
                        )}
                      </div>
                    </div>

                    {/* LOTS SECTION */}
                    <div className="p-6 bg-white/40 border border-[#141414] rounded-2xl space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          Lot Numbers & Expiry Dates
                        </h3>
                        <button
                          type="button"
                          onClick={() => setEditingItem(prev => prev ? ({ 
                            ...prev, 
                            lots: [...(prev.lots || []), { id: Math.random().toString(36).substr(2, 9), lotNumber: '', expiryDate: '' }]
                          }) : null)}
                          className="flex items-center gap-1 px-3 py-1 bg-[#141414] text-[#E4E3E0] rounded-full text-[10px] font-bold uppercase tracking-wider hover:opacity-90 transition-all"
                        >
                          <Plus className="w-3 h-3" />
                          Add Lot
                        </button>
                      </div>

                      <div className="space-y-3">
                        {(editingItem.lots || []).map((lot, index) => (
                          <div key={lot.id} className="grid grid-cols-2 gap-3 items-end p-4 bg-white/50 rounded-xl border border-[#141414]/5">
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Lot Number {index + 1}</label>
                              <input
                                type="text"
                                placeholder="Lot #"
                                value={lot.lotNumber}
                                onChange={(e) => {
                                  const newLots = [...(editingItem.lots || [])];
                                  newLots[index].lotNumber = e.target.value;
                                  setEditingItem(prev => prev ? ({ ...prev, lots: newLots }) : null);
                                }}
                                className="w-full bg-white border border-[#141414] p-2 rounded-lg outline-none text-xs"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <label className="block text-[10px] font-bold uppercase tracking-widest mb-1 opacity-50">Expiry Date</label>
                                <div className="relative">
                                  <input
                                    type="date"
                                    value={lot.expiryDate}
                                    onChange={(e) => {
                                      const newLots = [...(editingItem.lots || [])];
                                      newLots[index].expiryDate = e.target.value;
                                      setEditingItem(prev => prev ? ({ ...prev, lots: newLots }) : null);
                                    }}
                                    className="w-full bg-white border border-[#141414] p-2 pr-8 rounded-lg outline-none text-xs"
                                  />
                                  <Calendar className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none" />
                                </div>
                              </div>
                              {(editingItem.lots || []).length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingItem(prev => prev ? ({ 
                                      ...prev, 
                                      lots: prev.lots?.filter((_, i) => i !== index)
                                    }) : null);
                                  }}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        {(!editingItem.lots || editingItem.lots.length === 0) && (
                          <p className="text-[10px] opacity-40 italic text-center py-4">No lots added. Click "Add Lot" to track by lot number.</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-span-2 space-y-3">
                  <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                    <input 
                      type="checkbox" 
                      id="editEnablePOS"
                      checked={!!editingItem.enablePOS}
                      onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, enablePOS: e.target.checked }) : null)}
                      className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                    />
                    <label htmlFor="editEnablePOS" className="text-sm font-bold cursor-pointer select-none">
                      Enable POS
                      <span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                        Make this item available in the Point of Sale system
                      </span>
                    </label>
                  </div>

                  {editingItem.enablePOS && (
                    <div className="p-6 bg-white/40 border border-[#141414] rounded-2xl space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">
                      <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        POS Options
                      </h3>
                      
                      {/* Image Upload */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-widest opacity-50">Product Image</label>
                        <div className="flex items-center gap-4">
                          <div className="w-24 h-24 border-2 border-dashed border-[#141414]/20 rounded-xl flex items-center justify-center overflow-hidden bg-white/50">
                            {editingItem.imageUrl ? (
                              <img src={editingItem.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                              <Package className="w-8 h-8 opacity-20" />
                            )}
                          </div>
                          <div className="flex-1 space-y-2">
                            <input 
                              type="file" 
                              accept="image/*"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  if (getLocalAuthToken()) {
                                    const imageUrl = await uploadLocalItemImage(file);
                                    setEditingItem(prev => prev ? ({ ...prev, imageUrl }) : null);
                                  } else {
                                    const storageRef = ref(storage, `items/${Date.now()}-${file.name}`);
                                    const snapshot = await uploadBytes(storageRef, file);
                                    const url = await getDownloadURL(snapshot.ref);
                                    setEditingItem(prev => prev ? ({ ...prev, imageUrl: url }) : null);
                                  }
                                  notify.confirm('Image uploaded successfully');
                                } catch (err) {
                                  console.error(err);
                                  notify.error('Failed to upload image');
                                }
                              }}
                              className="hidden" 
                              id="editItemImage" 
                            />
                            <label 
                              htmlFor="editItemImage"
                              className="inline-block px-4 py-2 bg-[#141414] text-[#E4E3E0] text-xs font-bold rounded-full cursor-pointer hover:opacity-90 transition-all"
                            >
                              Upload Image
                            </label>
                            <p className="text-[10px] opacity-50">Recommended size: 800x800px. Max 2MB.</p>
                          </div>
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="space-y-2">
                        <label className="block text-xs font-bold uppercase tracking-widest opacity-50">Tags</label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {editingItem.tags?.map((tag, idx) => (
                            <span key={idx} className="px-2 py-1 bg-[#141414] text-[#E4E3E0] text-[10px] font-bold rounded-full flex items-center gap-1">
                              {tag}
                              <button 
                                type="button"
                                onClick={() => setEditingItem(prev => prev ? ({ ...prev, tags: prev.tags?.filter((_, i) => i !== idx) }) : null)}
                                className="hover:text-red-400"
                              >
                                <XCircle className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <input 
                          type="text"
                          placeholder="Add tag and press Enter..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = e.currentTarget.value.trim();
                              if (val && !editingItem.tags?.includes(val)) {
                                setEditingItem(prev => prev ? ({ ...prev, tags: [...(prev.tags || []), val] }) : null);
                                e.currentTarget.value = '';
                              }
                            }
                          }}
                          className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none text-sm"
                        />
                      </div>

                      {/* Store Availability */}
                      <div className="space-y-4 pt-4 border-t border-[#141414]/10">
                        <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          Store Availability & Quantity
                        </h3>
                        <div className="space-y-3">
                          {stores.map(store => {
                            const availability = editingItem.storeAvailability?.find(sa => sa.storeId === store.id);
                            return (
                              <div key={store.id} className="flex items-center gap-4 bg-white/30 p-3 rounded-xl border border-[#141414]/5">
                                <div className="flex-1">
                                  <label className="flex items-center gap-3 cursor-pointer select-none">
                                    <input 
                                      type="checkbox"
                                      checked={availability?.enabled ?? false}
                                      onChange={(e) => {
                                        const sa = editingItem.storeAvailability || [];
                                        const exists = sa.find(item => item.storeId === store.id);
                                        const updated = exists 
                                          ? sa.map(item => item.storeId === store.id ? { ...item, enabled: e.target.checked } : item)
                                          : [...sa, { storeId: store.id, storeName: store.name, quantity: 0, enabled: e.target.checked }];
                                        setEditingItem(prev => prev ? ({ ...prev, storeAvailability: updated }) : null);
                                      }}
                                      className="w-4 h-4 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                                    />
                                    <span className="text-sm font-bold">{store.name} {(store as any).isWarehouse ? '(Warehouse)' : '(Store)'}</span>
                                  </label>
                                </div>
                                {availability?.enabled && (
                                  <div className="flex items-center gap-2">
                                    <label className="text-[10px] font-bold uppercase opacity-50">Stock Quantity</label>
                                    <input 
                                      type="number"
                                      value={availability.quantity}
                                      onChange={(e) => {
                                        const sa = editingItem.storeAvailability || [];
                                        const updated = sa.map(item => item.storeId === store.id ? { ...item, quantity: parseInt(e.target.value) || 0 } : item);
                                        setEditingItem(prev => prev ? ({ ...prev, storeAvailability: updated }) : null);
                                      }}
                                      className="w-20 bg-white/50 border border-[#141414] p-2 rounded-lg text-xs outline-none text-center"
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Recipe Management */}
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            Recipe Management
                          </h3>
                          <button 
                            type="button"
                            onClick={() => setEditingItem(prev => prev ? ({ 
                              ...prev, 
                              recipes: [...(prev.recipes || []), { id: Math.random().toString(36).substr(2, 9), name: '', isActive: prev.recipes?.length === 0, components: [] }] 
                            }) : null)}
                            className="text-[10px] font-bold underline hover:opacity-70"
                          >
                            + Add New Recipe
                          </button>
                        </div>

                        <div className="space-y-4">
                          {editingItem.recipes?.map((recipe, rIdx) => (
                            <div key={recipe.id} className="p-4 bg-white/30 border border-[#141414]/10 rounded-xl space-y-4">
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex-1 flex items-center gap-4">
                                  <input 
                                    type="text"
                                    placeholder="Recipe Name (e.g. Standard, Premium)"
                                    value={recipe.name}
                                    onChange={(e) => setEditingItem(prev => prev ? ({
                                      ...prev,
                                      recipes: prev.recipes?.map((r, i) => i === rIdx ? { ...r, name: e.target.value } : r)
                                    }) : null)}
                                    className="flex-1 bg-white/50 border border-[#141414] p-2 rounded-lg text-xs outline-none font-bold"
                                  />
                                  <div className="flex items-center gap-2">
                                    <input 
                                      type="checkbox"
                                      id={`edit-recipe-active-${rIdx}`}
                                      checked={recipe.isActive}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setEditingItem(prev => prev ? ({
                                            ...prev,
                                            recipes: prev.recipes?.map((r, i) => ({ ...r, isActive: i === rIdx }))
                                          }) : null);
                                        }
                                      }}
                                      className="w-4 h-4 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                                    />
                                    <label htmlFor={`edit-recipe-active-${rIdx}`} className="text-[10px] font-bold uppercase tracking-widest opacity-50 cursor-pointer">Active</label>
                                  </div>
                                </div>
                                <button 
                                  type="button"
                                  onClick={() => setEditingItem(prev => prev ? ({ ...prev, recipes: prev.recipes?.filter((_, i) => i !== rIdx) }) : null)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-50">Components</label>
                                  <button 
                                    type="button"
                                    onClick={() => setEditingItem(prev => prev ? ({
                                      ...prev,
                                      recipes: prev.recipes?.map((r, i) => i === rIdx ? { ...r, components: [...r.components, { itemId: '', name: '', quantity: 1, unit: 'pcs' }] } : r)
                                    }) : null)}
                                    className="text-[9px] font-bold underline hover:opacity-70"
                                  >
                                    + Add Component
                                  </button>
                                </div>
                                <div className="space-y-2">
                                  {recipe.components.map((ingredient, cIdx) => (
                                    <div key={cIdx} className="flex gap-2 items-start">
                                      <select 
                                        value={ingredient.itemId}
                                        onChange={(e) => {
                                          const selected = items.find(i => i.id === e.target.value);
                                          setEditingItem(prev => prev ? ({
                                            ...prev,
                                            recipes: prev.recipes?.map((r, i) => i === rIdx ? {
                                              ...r,
                                              components: r.components.map((c, j) => j === cIdx ? { ...c, itemId: e.target.value, name: selected?.name || '' } : c)
                                            } : r)
                                          }) : null);
                                        }}
                                        className="flex-1 bg-white/50 border border-[#141414] p-2 rounded-lg text-[10px] outline-none"
                                      >
                                        <option value="">Select Item</option>
                                        {items.filter(i => i.id !== editingItem.id).map(i => (
                                          <option key={i.id} value={i.id}>{i.name} ({i.code})</option>
                                        ))}
                                      </select>
                                      <input 
                                        type="number"
                                        placeholder="Qty"
                                        value={isNaN(ingredient.quantity) ? '' : ingredient.quantity}
                                        onChange={(e) => setEditingItem(prev => prev ? ({
                                          ...prev,
                                          recipes: prev.recipes?.map((r, i) => i === rIdx ? {
                                            ...r,
                                            components: r.components.map((c, j) => j === cIdx ? { ...c, quantity: parseFloat(e.target.value) || 0 } : c)
                                          } : r)
                                        }) : null)}
                                        className="w-16 bg-white/50 border border-[#141414] p-2 rounded-lg text-[10px] outline-none text-center"
                                      />
                                      <select
                                        value={ingredient.unit || ''}
                                        onChange={(e) => setEditingItem(prev => prev ? ({
                                          ...prev,
                                          recipes: prev.recipes?.map((r, i) => i === rIdx ? {
                                            ...r,
                                            components: r.components.map((c, j) => j === cIdx ? { ...c, unit: e.target.value } : c)
                                          } : r)
                                        }) : null)}
                                        className="w-24 bg-white/50 border border-[#141414] p-2 rounded-lg text-[10px] outline-none"
                                      >
                                        <option value="">UOM</option>
                                        {recipeUomOptions(ingredient.itemId, ingredient.unit).legacy && <option value={ingredient.unit}>{ingredient.unit} (legacy)</option>}
                                        {recipeUomOptions(ingredient.itemId, ingredient.unit).options.map(unit => <option key={unit.id} value={unit.name}>{unit.name}</option>)}
                                      </select>
                                      <button 
                                        type="button"
                                        onClick={() => setEditingItem(prev => prev ? ({
                                          ...prev,
                                          recipes: prev.recipes?.map((r, i) => i === rIdx ? {
                                            ...r,
                                            components: r.components.filter((_, j) => j !== cIdx)
                                          } : r)
                                        }) : null)}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                      >
                                        <XCircle className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Variant Management */}
                      <div className="space-y-4 pt-4 border-t border-[#141414]/10">
                        <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                          <input 
                            type="checkbox" 
                            id="editHasVariants"
                            checked={!!editingItem.hasVariants}
                            onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, hasVariants: e.target.checked }) : null)}
                            className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                          />
                          <label htmlFor="editHasVariants" className="text-sm font-bold cursor-pointer select-none">
                            Enable Variants
                            <span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                              Add options like size or color to create multiple versions of this product
                            </span>
                          </label>
                        </div>

                        {editingItem.hasVariants && (
                          <div className="space-y-6 p-4 bg-white/30 border border-[#141414]/10 rounded-xl animate-in fade-in slide-in-from-top-2">
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <label className="text-xs font-bold uppercase tracking-widest opacity-50">Options</label>
                                <button 
                                  type="button"
                                  onClick={() => setEditingItem(prev => prev ? ({
                                    ...prev,
                                    variantOptions: [...(prev.variantOptions || []), { name: '', values: [] }]
                                  }) : null)}
                                  className="text-[10px] font-bold underline hover:opacity-70"
                                >
                                  + Add Option
                                </button>
                              </div>

                              <div className="space-y-4">
                                {editingItem.variantOptions?.map((option, oIdx) => (
                                  <div key={oIdx} className="space-y-2 p-3 bg-white/50 rounded-lg border border-[#141414]/5">
                                    <div className="flex gap-2">
                                      <input 
                                        type="text"
                                        placeholder="Option Name (e.g. Color, Size)"
                                        value={option.name}
                                        onChange={(e) => setEditingItem(prev => prev ? ({
                                          ...prev,
                                          variantOptions: prev.variantOptions?.map((o, i) => i === oIdx ? { ...o, name: e.target.value } : o)
                                        }) : null)}
                                        className="flex-1 bg-white/50 border border-[#141414] p-2 rounded-lg text-xs outline-none font-bold"
                                      />
                                      <button 
                                        type="button"
                                        onClick={() => setEditingItem(prev => prev ? ({
                                          ...prev,
                                          variantOptions: prev.variantOptions?.filter((_, i) => i !== oIdx)
                                        }) : null)}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </button>
                                    </div>
                                    <div className="space-y-2">
                                      <div className="flex flex-wrap gap-2">
                                        {option.values.map((val, vIdx) => (
                                          <span key={vIdx} className="px-2 py-1 bg-[#141414] text-[#E4E3E0] text-[9px] font-bold rounded-full flex items-center gap-1">
                                            {val}
                                            <button 
                                              type="button"
                                              onClick={() => setEditingItem(prev => prev ? ({
                                                ...prev,
                                                variantOptions: prev.variantOptions?.map((o, i) => i === oIdx ? { ...o, values: o.values.filter((_, j) => j !== vIdx) } : o)
                                              }) : null)}
                                              className="hover:text-red-400"
                                            >
                                              <XCircle className="w-3 h-3" />
                                            </button>
                                          </span>
                                        ))}
                                      </div>
                                      <input 
                                        type="text"
                                        placeholder="Add value and press Enter..."
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const val = e.currentTarget.value.trim();
                                            if (val && !option.values.includes(val)) {
                                              setEditingItem(prev => prev ? ({
                                                ...prev,
                                                variantOptions: prev.variantOptions?.map((o, i) => i === oIdx ? { ...o, values: [...o.values, val] } : o)
                                              }) : null);
                                              e.currentTarget.value = '';
                                            }
                                          }
                                        }}
                                        className="w-full bg-white/50 border border-[#141414] p-2 rounded-lg text-[10px] outline-none"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <button 
                                type="button"
                                onClick={() => {
                                  const generated = generateVariants(
                                    editingItem.variantOptions || [], 
                                    editingItem.unitPrice || 0, 
                                    editingItem.costPrice || 0,
                                    editingItem.code || 'VAR'
                                  );
                                  setEditingItem(prev => prev ? ({ ...prev, variants: generated }) : null);
                                }}
                                className="w-full py-2 bg-[#141414] text-[#E4E3E0] text-[10px] font-bold rounded-lg hover:opacity-90 transition-all uppercase tracking-widest"
                              >
                                Generate Variants
                              </button>
                            </div>

                            {editingItem.variants && editingItem.variants.length > 0 && (
                              <div className="space-y-3">
                                <label className="text-xs font-bold uppercase tracking-widest opacity-50">Generated Variants</label>
                                <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar pr-2">
                                  <div className="grid grid-cols-7 gap-2 px-2 py-1 text-[9px] font-bold uppercase tracking-wider opacity-50">
                                    <div className="flex items-center justify-center">Save</div>
                                    <div className="col-span-2">Variant</div>
                                    <div>Cost Price</div>
                                    <div>Sell Price</div>
                                    <div>Barcode</div>
                                    <div className="text-center">Qty</div>
                                  </div>
                                  {(editingItem.variants || []).map((variant, vIdx) => (
                                    <div key={variant.id} className={cn(
                                      "grid grid-cols-7 gap-2 p-2 rounded-lg border items-center transition-all",
                                      variant.save ? "bg-white/50 border-[#141414]/5" : "bg-white/10 border-transparent opacity-50"
                                    )}>
                                      <div className="flex items-center justify-center">
                                        <input 
                                          type="checkbox"
                                          checked={!!variant.save}
                                          onChange={(e) => setEditingItem(prev => prev ? ({
                                            ...prev,
                                            variants: prev.variants?.map((v, i) => i === vIdx ? { ...v, save: e.target.checked } : v)
                                          }) : null)}
                                          className="w-4 h-4 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                                        />
                                      </div>
                                      <div className="col-span-2 text-[10px] font-bold truncate">
                                        {variant.name}
                                        <span className="block text-[8px] opacity-40 font-mono">{variant.sku}</span>
                                      </div>
                                      <input 
                                        type="number"
                                        placeholder="Cost"
                                        value={isNaN(variant.costPrice) ? '' : variant.costPrice}
                                        onChange={(e) => setEditingItem(prev => prev ? ({
                                          ...prev,
                                          variants: prev.variants?.map((v, i) => i === vIdx ? { ...v, costPrice: parseFloat(e.target.value) || 0 } : v)
                                        }) : null)}
                                        className="bg-white/50 border border-[#141414] p-1 rounded text-[10px] outline-none text-center"
                                      />
                                      <input 
                                        type="number"
                                        placeholder="Sell"
                                        value={isNaN(variant.unitPrice) ? '' : variant.unitPrice}
                                        onChange={(e) => setEditingItem(prev => prev ? ({
                                          ...prev,
                                          variants: prev.variants?.map((v, i) => i === vIdx ? { ...v, unitPrice: parseFloat(e.target.value) || 0 } : v)
                                        }) : null)}
                                        className="bg-white/50 border border-[#141414] p-1 rounded text-[10px] outline-none text-center"
                                      />
                                      <input 
                                        type="text"
                                        placeholder="Barcode"
                                        value={variant.barcode}
                                        onChange={(e) => setEditingItem(prev => prev ? ({
                                          ...prev,
                                          variants: prev.variants?.map((v, i) => i === vIdx ? { ...v, barcode: e.target.value } : v)
                                        }) : null)}
                                        className="bg-white/50 border border-[#141414] p-1 rounded text-[10px] outline-none"
                                      />
                                      <input 
                                        type="number"
                                        placeholder="Qty"
                                        value={isNaN(variant.quantity) ? '' : variant.quantity}
                                        onChange={(e) => setEditingItem(prev => prev ? ({
                                          ...prev,
                                          variants: prev.variants?.map((v, i) => i === vIdx ? { ...v, quantity: parseInt(e.target.value) || 0 } : v)
                                        }) : null)}
                                        className="bg-white/50 border border-[#141414] p-1 rounded text-[10px] outline-none text-center"
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Additional POS Options */}
                      <div className="space-y-3 pt-4 border-t border-[#141414]/10">
                        <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                          <input 
                            type="checkbox" 
                            id="editAllowNegativeStock"
                            checked={!!editingItem.allowNegativeStock}
                            onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, allowNegativeStock: e.target.checked }) : null)}
                            className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                          />
                          <label htmlFor="editAllowNegativeStock" className="text-sm font-bold cursor-pointer select-none">
                            Allow Negative Stock
                            <span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                              This will allow the product to be sold in negative even with no stock
                            </span>
                          </label>
                        </div>
                        <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                          <input 
                            type="checkbox" 
                            id="editHasExpiry"
                            checked={!!editingItem.hasExpiry}
                            onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, hasExpiry: e.target.checked }) : null)}
                            className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                          />
                          <label htmlFor="editHasExpiry" className="text-sm font-bold cursor-pointer select-none">
                            Product Has Expiry
                            <span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                              This will toggle the product that it will have an expiry date that need to be tracked
                            </span>
                          </label>
                        </div>
                        <div className="flex items-center gap-3 bg-white/30 p-4 rounded-xl border border-[#141414]/10">
                          <input 
                            type="checkbox" 
                            id="editHideFromSellScreen"
                            checked={!!editingItem.hideFromSellScreen}
                            onChange={(e) => setEditingItem(prev => prev ? ({ ...prev, hideFromSellScreen: e.target.checked }) : null)}
                            className="w-5 h-5 rounded border-[#141414] text-[#141414] focus:ring-[#141414]"
                          />
                          <label htmlFor="editHideFromSellScreen" className="text-sm font-bold cursor-pointer select-none">
                            Hide From Sell Screen
                            <span className="block text-[10px] opacity-50 font-normal uppercase tracking-wider mt-0.5">
                              The product will be hidden to be sold in sell screen but will still be visible here in products and composite component
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <DocumentChatterPanel
                orgId={orgId}
                entityId={editingItem.id}
                entityType="Product"
                locale={language === 'ar' ? 'ar' : 'en'}
                className="mt-8"
              />

              <div className="flex gap-4 pt-4 border-t border-[#141414]">
                <div className="flex-1 flex gap-4">
                  <button 
                    type="button"
                    onClick={() => setEditingItem(null)}
                    className="flex-1 py-3 rounded-full border border-[#141414] font-bold hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="flex-1 py-3 rounded-full bg-[#141414] text-[#E4E3E0] font-bold hover:opacity-90 transition-all"
                  >
                    Update Item
                  </button>
                </div>
              </div>

              {/* Action Log Section */}
              {editingItem.actionLog && editingItem.actionLog.length > 0 && (
                <div className="mt-8 pt-8 border-t border-[#141414]/10">
                  <h3 className="text-xs font-bold uppercase tracking-widest mb-4 opacity-50">Action Log</h3>
                  <div className="space-y-3">
                    {editingItem.actionLog.slice().reverse().map((log, idx) => (
                      <div key={idx} className="flex items-start justify-between text-[11px] bg-white/30 p-3 rounded-xl border border-[#141414]/5">
                        <div>
                          <p className="font-bold text-[#141414]">{log.action}</p>
                          {log.details && <p className="text-[10px] opacity-70 mb-1">{log.details}</p>}
                          <p className="opacity-60">{log.by} ({log.byEmail})</p>
                        </div>
                        <div className="text-right opacity-50 font-mono">
                          {log.at?.toDate ? log.at.toDate().toLocaleString() : new Date(log.at).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
      {/* Item Import Modal */}
      {isLotModalOpen && (
        <div
          className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsLotModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold italic serif">{editingLot ? 'Edit Lot Definition' : 'New Lot Definition'}</h2>
              <button onClick={() => setIsLotModalOpen(false)} className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleLotSubmit} className="space-y-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Product</label>
                <select 
                  value={lotFormData.productId}
                  onChange={(e) => setLotFormData(prev => ({ ...prev, productId: e.target.value }))}
                  className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none font-bold text-sm"
                >
                  <option value="">Select Product...</option>
                  {items.filter(i => i.type === 'product').map(item => (
                    <option key={item.id} value={item.id}>{item.name} {item.code ? `(${item.code})` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Lot Number *</label>
                <input 
                  type="text" 
                  required
                  value={lotFormData.lotNumber}
                  onChange={(e) => setLotFormData(prev => ({ ...prev, lotNumber: e.target.value }))}
                  className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Expiry Date</label>
                <input 
                  type="date" 
                  value={lotFormData.expiryDate}
                  onChange={(e) => setLotFormData(prev => ({ ...prev, expiryDate: e.target.value }))}
                  className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Notes</label>
                <textarea 
                  rows={2}
                  value={lotFormData.notes}
                  onChange={(e) => setLotFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none resize-none"
                />
              </div>
              <div className="flex gap-4 pt-4 border-t border-[#141414]">
                <button type="button" onClick={() => setIsLotModalOpen(false)} className="flex-1 py-3 rounded-full border border-[#141414] font-bold">Cancel</button>
                <button type="submit" className="flex-1 py-3 rounded-full bg-[#141414] text-[#E4E3E0] font-bold">{editingLot ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isCategoryModalOpen && (
        <div
          className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsCategoryModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold italic serif">{editingCategory ? 'Edit Category' : 'New Category'}</h2>
              <button onClick={() => setIsCategoryModalOpen(false)} className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleCategorySubmit} className="space-y-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Category Name *</label>
                <input 
                  type="text" 
                  required
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none font-bold"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Description</label>
                <textarea 
                  rows={3}
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none resize-none"
                />
              </div>
              <div className="flex gap-4 pt-4 border-t border-[#141414]">
                <button type="button" onClick={() => setIsCategoryModalOpen(false)} className="flex-1 py-3 rounded-full border border-[#141414] font-bold">Cancel</button>
                <button type="submit" className="flex-1 py-3 rounded-full bg-[#141414] text-[#E4E3E0] font-bold">{editingCategory ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isWarehouseModalOpen && (
        <div
          className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsWarehouseModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-3xl p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold italic serif">{editingWarehouse ? 'Edit Warehouse' : 'New Warehouse'}</h2>
              <button onClick={() => setIsWarehouseModalOpen(false)} className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleWarehouseSubmit} className="space-y-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Warehouse Name *</label>
                <input 
                  type="text" 
                  required
                  value={warehouseFormData.name}
                  onChange={(e) => setWarehouseFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none font-bold"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Address</label>
                <textarea 
                  rows={2}
                  value={warehouseFormData.address}
                  onChange={(e) => setWarehouseFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">Status</label>
                <select 
                  value={warehouseFormData.status}
                  onChange={(e) => setWarehouseFormData(prev => ({ ...prev, status: e.target.value as any }))}
                  className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none font-bold"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="flex gap-4 pt-4 border-t border-[#141414]">
                <button type="button" onClick={() => setIsWarehouseModalOpen(false)} className="flex-1 py-3 rounded-full border border-[#141414] font-bold">Cancel</button>
                <button type="submit" className="flex-1 py-3 rounded-full bg-[#141414] text-[#E4E3E0] font-bold">{editingWarehouse ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isImportModalOpen && (
        <ItemImport 
          orgId={orgId}
          onClose={() => setIsImportModalOpen(false)}
          onComplete={() => {
            setIsImportModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
